import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  effect,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import type { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { ConfirmationService, type MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { Menu, MenuModule } from 'primeng/menu';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import {
  customerInvoiceReadinessMessage,
  missingCustomerInvoiceFields
} from '../../core/customer-invoice-readiness';
import { finalize, catchError, of, merge, switchMap, throwError } from 'rxjs';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';
import {
  SaleService,
  CreateSaleRequest,
  CreateSaleBillingTerms,
  CreateSaleLineRequest,
  CreateSaleLineTaxRequest,
  PreviewSaleLineRequest,
  SaleDTO,
  SaleLineDTO,
  ProductDTO as SaleProductDTO,
  CollectionMethod,
  TaxApplicationType,
  type MeasureUnitDTO as CoreMeasureUnitDTO,
  type TaxProductCodeDTO as CoreTaxProductCodeDTO
} from '../../services/sale.service';
import { CurrencyService, CurrencyDTO } from '../../services/currency.service';
import { CustomerService, CustomerDTO } from '../../services/customer.service';
import { CompanyService } from '../../services/company.service';
import { ProductService, ProductDTO } from '../../services/product.service';
import { MeasureUnitService, MeasureUnitDTO } from '../../services/measure-unit.service';
import { TaxProductCodeService, TaxProductCodeDTO } from '../../services/tax-product-code.service';
import { TaxService, TaxDTO } from '../../services/tax.service';
import { SATObjetoImpService, SATObjetoImpDTO } from '../../services/sat-objeto-imp.service';
import { SATCFDIUseService } from '../../services/sat-cfdi-use.service';
import { PaymentFormService } from '../../services/payment-form.service';
import { PaymentMethodService } from '../../services/payment-method.service';
import { InvoiceService } from '../../services/invoice.service';
import { ChargeService } from '../../services/charge.service';
import {
  ChargeOperationalStatusEnum,
  CollectionMethodEnum,
  EntityState,
  InvoiceStatusEnum,
  PaymentRecordMethodEnum,
  PaymentRecordStatusEnum,
  SalePipelineStatusEnum,
  SaleProcessStatusEnum,
  toLineTaxApplicationTypeForCreate,
  CreateSubscriptionRequest,
  type DelinquencyStatusEnumValue,
  type InvoiceStatusEnumValue,
  type PaymentRecordMethodEnumValue,
  type PaymentRecordStatusEnumValue,
  type SalePipelineStatusEnumValue,
  type SubscriptionDTO,
  type UpdateSubscriptionRequest,
  type AdvanceBillingCycleSaleSummaryDTO,
  type AdvanceBillingCyclesResultDTO,
  SubscriptionStatusEnum,
  type BillingCycleChangeModeValue,
  type FrequencyEnumValue
} from '../../core/models';
import { SubscriptionService } from '../../services/subscription.service';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import {
  formatApiDateOnly,
  formValueToApiDateTime,
  formValueToLocalDate,
  parseApiCalendarDate,
  parseApiDateTime,
  parseLocalDate
} from '../../core/date-utils';
import {
  catchUpCyclesUnitLabel,
  computeNextBillingDate,
  previewCatchUpBillingCycles,
  requiresCatchUpConfirmation,
  resolveCatchUpBillingState,
  type CatchUpBillingCyclesPreview
} from '../../core/subscription-billing-cycle.utils';
import {
  lineSha256Hash,
  subscriptionSha256Hash
} from '../../core/billing-hash.utils';
import {
  additionalFieldControl,
  rebuildAdditionalFieldsForm,
  serializeAdditionalFields,
  sortDynamicFields,
  dynamicFieldTypeLabel
} from '../../core/dynamic-fields.utils';
import type {
  CompanyDTO,
  DynamicField,
  DynamicFieldType,
  PaymentFormDTO,
  PaymentMethodDTO,
  SATCFDIUseDTO,
  ChargeDTO,
  PaymentAttemptDTO
} from '../../core/models';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { PlanService } from '../../services/plan.service';
import type { PlanDTO, PlanItemDTO } from '../../core/models';
import { SALE_LINE_DESCRIPTION_VARIABLES } from '../../core/sale-line-description-variables';

export interface SaleLineVm {
  tempId?: string;
  id?: number;
  /** Hash financiero persistido (API); referencia para detectar cambios por línea. */
  hash?: string;
  productId?: number;
  product?: SaleProductDTO | ProductDTO;
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  measureUnitId?: number;
  measureUnit?: CoreMeasureUnitDTO;
  taxProductCodeId?: number;
  taxProductCode?: CoreTaxProductCodeDTO;
  satObjetoImpId?: string;
  satObjetoImp?: SATObjetoImpDTO;
  taxExempt?: boolean;
  taxInclusive?: boolean;
  taxes: CreateSaleLineTaxRequest[];
}

@Component({
  selector: 'app-subscription-sale-form',
  templateUrl: './subscription-sale-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
      /* FloatLabel + InputGroup: el label no debe quedar bajo el borde */
      :host ::ng-deep .dyn-field-floatlabel.p-floatlabel {
        overflow: visible;
      }
      :host ::ng-deep .dyn-field-floatlabel .p-inputgroup {
        overflow: visible;
      }
      :host ::ng-deep .dyn-field-floatlabel > label {
        z-index: 3;
        background-color: var(--p-content-background, #ffffff);
        padding-inline: 0.35rem;
        margin-inline-start: 0.5rem;
        line-height: 1;
        pointer-events: none;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-sale-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-sale-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    FloatLabelModule,
    SelectModule,
    AutoCompleteModule,
    ButtonModule,
    SplitButtonModule,
    CardModule,
    TabsModule,
    DatePickerModule,
    TooltipModule,
    CheckboxModule,
    ConfirmDialogModule,
    DialogModule,
    SelectButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    MenubarModule,
    MenuModule,
    TagModule,
    RouterLink,
    DrawerModule,
    CustomerReadComponent,
    SaleReadComponent,
    EntityAuditPanelComponent
  ],
})
export class SubscriptionSaleFormComponent {
  private fb = inject(FormBuilder);
  private sanitizer = inject(DomSanitizer);
  private saleService = inject(SaleService);
  private subscriptionService = inject(SubscriptionService);
  private planService = inject(PlanService);
  private invoiceService = inject(InvoiceService);
  private chargeService = inject(ChargeService);
  private currencyService = inject(CurrencyService);
  private customerService = inject(CustomerService);
  private companyService = inject(CompanyService);
  private productService = inject(ProductService);
  private measureUnitService = inject(MeasureUnitService);
  private taxProductCodeService = inject(TaxProductCodeService);
  private taxService = inject(TaxService);
  private satObjetoImpService = inject(SATObjetoImpService);
  private satCfdiUseService = inject(SATCFDIUseService);
  private paymentFormService = inject(PaymentFormService);
  private paymentMethodService = inject(PaymentMethodService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  /** Expuesto al template para comparar métodos de cobro. */
  readonly CollectionMethodEnum = CollectionMethodEnum;
  /** Formatea fechas solo-día del API sin desfase UTC (uso en template). */
  readonly formatApiDateOnly = formatApiDateOnly;

  @ViewChild('productLineRef') productLineRef?: ElementRef<HTMLElement>;
  @ViewChild('chargeActionsMenu') chargeActionsMenu?: Menu;
  @ViewChild('lineDescriptionVarMenu') lineDescriptionVarMenu?: Menu;

  /**
   * Formulario raíz con grupos anidados alineados a las secciones del tab General (validación por sección):
   * - `general`: cliente, moneda, tipo de venta, método de cobro, estados, notas y totales.
   * - `paymentTerms`: cuotas, frecuencia, cobro y plazos.
   * - `invoicing`: datos fiscales opcionales y estrategia de emisión.
   */
  saleForm: FormGroup;
  /** Sección General del tab General. */
  generalForm: FormGroup;
  /** Sección Facturación (dentro de General). */
  invoicingForm: FormGroup;
  /** Sección Términos de pago (dentro de General). */
  paymentTermsForm: FormGroup;
  /** Campos adicionales dinámicos (configuración de empresa). */
  additionalFieldsForm: FormGroup;
  /** Definición de campos adicionales cargada de /companies/me. */
  dynamicFields = signal<DynamicField[]>([]);
  /** Forzar reactividad de canSaveDraft al cambiar campos adicionales. */
  private additionalFieldsRefresh = signal(0);
  loading = signal(false);
  /** Overlay de carga inicial o refresh de la suscripción (no guardado ni otras acciones). */
  formDataLoading = signal(false);
  /** True mientras se genera el hash para copiar el enlace público del recibo. */
  receiptLinkCopyLoading = signal(false);
  downloadPdfLoading = signal(false);
  downloadXmlLoading = signal(false);
  /** Carga del PDF para abrir el visor (clic principal del split CFDI). */
  cfdiPdfViewerOpening = signal(false);
  cfdiPdfViewerVisible = signal(false);
  cfdiPdfViewerSafeUrl = signal<SafeResourceUrl | null>(null);
  /** Timbrado POST /sales/{id}/stamp. */
  stampSaleLoading = signal(false);
  /** PUT /sales/{id}/invoicing */
  invoicingProfileUpdateLoading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  /** El cliente de una suscripción existente no se puede cambiar. */
  isCustomerLocked = computed(() => this.isEditMode());

  /**
   * Tras crear la suscripción no se pueden modificar plan ni fecha de inicio de cobro.
   */
  isExistingSubscriptionEdit = computed(() => this.editingSubscriptionId() != null);

  /** Bloqueo de plan y fecha inicio de cobro (edición o formulario readonly). */
  isSubscriptionImmutableFieldsLocked = computed(
    () => this.isExistingSubscriptionEdit() || this.isReadonly()
  );

  /**
   * Método de cobro inmutable si es Cargo Recurrente o el formulario es readonly.
   * Entre Liga de pago y Manual sí se permite cambiar en edición.
   */
  isCollectionMethodLocked = computed(() => {
    if (this.isReadonly()) return true;
    if (!this.isExistingSubscriptionEdit()) return false;
    return this.currentSubscription()?.collectionMethod === CollectionMethodEnum.RECURRING_CHARGE;
  });
  editingSaleId = signal<number | null>(null);
  /** Id de suscripción en edición (alta vía API de suscripciones). */
  editingSubscriptionId = signal<number | null>(null);
  currentSubscription = signal<SubscriptionDTO | null>(null);
  /** Hash financiero persistido al cargar/guardar la suscripción (API). */
  savedSubscriptionHash = signal<string | null>(null);
  /** Hash financiero calculado desde el formulario actual. */
  currentSubscriptionHash = signal<string | null>(null);
  /** True tras modificar líneas o montos comerciales en edición. */
  private subscriptionBillingDirty = signal(false);
  planOptions = signal<PlanDTO[]>([]);
  selectedPlan = signal<PlanDTO | null>(null);
  currentSale = signal<SaleDTO | null>(null);

  /** True cuando la factura está emitida/otro estado (no DRAFT): formulario y botón guardar deshabilitados. */
  isReadonly = computed(
    () =>
      this.isEditMode() &&
      this.currentSale() != null &&
      this.currentSale()?.status !== 'DRAFT'
  );

  /** En edición no hay mínimo: el datepicker oculta valores anteriores a `minDate`. */
  minDueDate = computed((): Date | undefined => undefined);

  /**
   * Ventas que se generarán si la fecha de inicio de cobro es anterior a hoy
   * (ciclos vencidos + ciclo en curso).
   */
  catchUpPreview = signal<CatchUpBillingCyclesPreview>({
    sales: [],
    nextBillingDate: null,
    grouped: false,
    catchUpCycleCount: 0,
    cycleLabels: [],
    regularCycleLabel: null
  });

  catchUpRequired = computed(() => requiresCatchUpConfirmation(this.catchUpPreview()));

  catchUpPreviewSales = computed(() => this.catchUpPreview().sales);

  /** Agrupar ciclos vencidos en una sola venta al crear la suscripción. */
  groupCatchUpCycles = signal(false);

  /** Con plan seleccionado: moneda, frecuencia y líneas quedan bloqueadas. */
  isPlanLocked = computed(() => this.selectedPlan() != null);

  /**
   * Cambio en el hash global de la suscripción respecto al persistido en API.
   */
  hasBillingImpact = computed(() => {
    if (!this.subscriptionBillingDirty() || !this.isExistingSubscriptionEdit() || this.isPlanLocked() || this.isReadonly()) {
      return false;
    }
    const baseline = this.savedSubscriptionHash();
    const current = this.currentSubscriptionHash();
    if (baseline == null || current == null) {
      return false;
    }
    return baseline !== current;
  });

  /** Readonly general o campos comerciales del plan. */
  isCommercialFieldsReadonly = computed(() => this.isReadonly() || this.isPlanLocked());

  /**
   * Permite usar «Facturar» (timbrar CFDI): `Sale.status` ISSUED, PARTIALLY_PAID o PAID
   * (alineado con `SaleStatus` en API).
   */
  canStampSaleCfdi = computed(() => {
    const sale = this.currentSale();
    if (sale?.invoice != null) return false;
    const s = sale?.status;
    return (
      s === InvoiceStatusEnum.ISSUED ||
      s === InvoiceStatusEnum.PARTIALLY_PAID ||
      s === InvoiceStatusEnum.PAID
    );
  });

  lines = signal<SaleLineVm[]>([]);
  /** Señal para forzar re-evaluación de canSaveDraft tras agregar/eliminar líneas. */
  private draftButtonRefresh = signal(0);
  /** Reactiva canSaveDraft ante cambios en el tab General (cliente, moneda, notas, totales ocultos). */
  private generalTabDraftRefresh = signal(0);

  /**
   * Guardado habilitado con datos del tab General: cliente, moneda, términos de pago,
   * facturación (si aplica) y campos adicionales.
   */
  canSaveDraft = computed(() => {
    this.lines();
    this.draftButtonRefresh();
    this.generalTabDraftRefresh();
    this.additionalFieldsRefresh();
    this.paymentTermsDraftRefresh();
    if (this.loading() || this.isReadonly()) return false;
    const v = this.generalForm.getRawValue();
    const hasCore =
      v.customerId != null &&
      v.currencyId != null &&
      v.status != null &&
      String(v.status).trim() !== '';
    return (
      hasCore &&
      this.generalForm.valid &&
      this.paymentTermsForm.valid &&
      this.invoicingForm.valid &&
      this.additionalFieldsForm.valid
    );
  });

  /** Las ventas no timbran CFDI por este flujo (solo facturas). */
  canEmitCfdi = computed(() => false);

  /**
   * Id de factura vinculada: respuesta de venta o primer cargo con `invoiceId` (p. ej. tras cargar recibos).
   */
  linkedInvoiceId = computed((): number | null => {
    const sale = this.currentSale();
    const fromSale = sale?.invoiceId ?? sale?.invoice?.id;
    if (fromSale != null && typeof fromSale === 'number' && !Number.isNaN(fromSale)) {
      return fromSale;
    }
    const fromCharge = this.charges().find((c) => c.invoiceId != null)?.invoiceId;
    if (fromCharge != null && typeof fromCharge === 'number' && !Number.isNaN(fromCharge)) {
      return fromCharge;
    }
    return null;
  });

  /** Id de suscripción vinculada a la venta (si existe). */
  linkedSubscriptionId = computed((): number | null => {
    const id = this.currentSale()?.subscription?.id;
    return id != null && typeof id === 'number' && !Number.isNaN(id) ? id : null;
  });

  linkedSubscriptionStatus = computed((): string | null => {
    return this.currentSale()?.subscription?.status ?? null;
  });

  /**
   * Token del enlace público de pago (`/public/subscriptions/{token}`).
   * Solo aplica a suscripciones con cobro recurrente.
   */
  subscriptionPublicToken = computed((): string | null => {
    const sub = this.currentSubscription() ?? this.currentSale()?.subscription ?? null;
    if (!sub?.token) return null;
    const method =
      sub.collectionMethod ??
      this.currentSale()?.collectionMethod ??
      null;
    if (method !== CollectionMethodEnum.RECURRING_CHARGE) return null;
    return String(sub.token);
  });

  lineModalOpen = signal(false);
  lineModalShown = signal(false);
  lineModalMode = signal<'create' | 'edit'>('create');
  /** Datos fiscales e impuestos del diálogo de línea: ocultos por defecto. */
  lineFiscalDetailsExpanded = signal(false);
  editingLineIndex = signal<number | null>(null);
  lineForm: FormGroup;
  lineSaving = signal(false);
  /** Subtotal calculado en el modal de línea (cantidad × precio). */
  lineSubtotal = signal(0);
  lineRemoving = signal(false);

  lineDescriptionVarMenuItems = computed((): MenuItem[] =>
    SALE_LINE_DESCRIPTION_VARIABLES.map((variable) => ({
      label: `${variable.label} (@${variable.key})`,
      title: variable.description,
      command: () => this.insertLineDescriptionVariable(variable.key)
    }))
  );

  /** Diálogo de confirmación al cerrar venta (ISSUED). */
  closeSaleConfirmVisible = signal(false);

  /** Diálogo de confirmación antes de timbrar CFDI (Facturar). */
  stampSaleConfirmVisible = signal(false);

  /** Diálogo de confirmación para cancelar la suscripción. */
  cancelSubscriptionConfirmVisible = signal(false);
  cancelSubscriptionLoading = signal(false);

  /**
   * Cancelar suscripción solo en estados operativos activos.
   * No cancela ventas ni cargos pendientes (solo cambia el status de la suscripción).
   */
  canCancelSubscription = computed(() => {
    const status = this.currentSubscription()?.status;
    return (
      status === SubscriptionStatusEnum.TRAILING ||
      status === SubscriptionStatusEnum.ACTIVE ||
      status === SubscriptionStatusEnum.PAST_DUE ||
      status === SubscriptionStatusEnum.PENDING_PAYMENT
    );
  });

  productOptions = signal<ProductDTO[]>([]);
  productLoading = signal(false);
  customerOptions = signal<CustomerDTO[]>([]);
  customerLoading = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  /** Cliente completo (régimen, país y direcciones) para validar autoInvoice. */
  invoiceCustomer = signal<CustomerDTO | null>(null);
  invoiceCustomerStatus = signal<'empty' | 'loading' | 'ready'>('empty');
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  isSaleDetailsOpen = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  saleDetailsLoading = signal(false);

  currencyOptions = signal<CurrencyDTO[]>([]);
  measureUnitOptions = signal<MeasureUnitDTO[]>([]);
  taxProductCodeOptions = signal<TaxProductCodeDTO[]>([]);
  satObjetoImpOptions = signal<SATObjetoImpDTO[]>([]);
  satCfdiUseOptions = signal<SATCFDIUseDTO[]>([]);
  taxOptions = signal<TaxDTO[]>([]);
  paymentFormOptions = signal<PaymentFormDTO[]>([]);
  paymentMethodOptions = signal<PaymentMethodDTO[]>([]);
  currencyLoading = signal(false);

  lineTaxModalOpen = signal(false);
  lineTaxModalShown = signal(false);
  lineTaxForm: FormGroup;
  lineTaxSaving = signal(false);
  editingLineTaxIndex = signal<number | null>(null);

  /** Índices de líneas con desglose de impuestos expandido */
  expandedLineIndices = signal<Set<number>>(new Set());

  activeTab = signal<string>('general');

  /** Reactiva botones de guardado ante cambios en términos de pago / facturación. */
  private paymentTermsDraftRefresh = signal(0);

  /**
   * Pie de guardado: exige datos generales, términos y facturación válidos.
   */
  footerDraftSubmitEnabled = computed(() => {
    this.paymentTermsDraftRefresh();
    if (this.loading() || this.isReadonly()) return false;
    return this.canSaveDraft();
  });

  charges = signal<ChargeDTO[]>([]);
  chargesLoading = signal(false);
  /** Ids de cargos con fila de intentos de pago expandida. */
  expandedChargeIds = signal<Set<number>>(new Set());
  /** Intentos de pago cacheados por chargeId (solo se cargan al expandir). */
  chargeAttemptsById = signal<Record<number, PaymentAttemptDTO[]>>({});
  /** chargeIds con carga de intentos en curso. */
  chargeAttemptsLoadingIds = signal<Set<number>>(new Set());
  /** Ítems del menú overlay de acciones por fila en Recibos de pago. */
  chargeRowMenuItems = signal<MenuItem[]>([]);
  /** Diálogo para reenviar correo de liga de pago. */
  recallEmailDialogVisible = signal(false);
  recallEmail = signal('');
  recallChargeId = signal<number | null>(null);
  recallLoading = signal(false);
  recallConvertsManualCharge = signal(false);

  /** Ventas asociadas a la suscripción (GET /sales?subscriptionId=). */
  subscriptionSales = signal<SaleDTO[]>([]);
  salesLoading = signal(false);
  private salesLoaded = false;

  /** Próxima venta programada (aún no creada); se muestra como fila fantasma en el tab Ventas. */
  nextScheduledSalePreview = computed(() => {
    const sub = this.currentSubscription();
    if (!sub?.nextBillingDate || sub.frequency === 'ONCE') return null;
    const status = sub.status;
    if (
      status === SubscriptionStatusEnum.CANCELED ||
      status === SubscriptionStatusEnum.CANCELLED ||
      status === SubscriptionStatusEnum.FINISHED ||
      status === SubscriptionStatusEnum.EXPIRED
    ) {
      return null;
    }
    const scheduledDate = parseLocalDate(sub.nextBillingDate);
    if (!scheduledDate) return null;
    return {
      scheduledDate,
      total: sub.total ?? 0,
      currency: sub.currency ?? 'MXN',
      daysUntilExpiry: sub.daysUntilExpiry ?? null
    };
  });

  showSalesTable = computed(
    () => this.subscriptionSales().length > 0 || this.nextScheduledSalePreview() != null
  );

  /**
   * Vencimiento del cobro: collectionStartDate + daysUntilExpiry (misma regla que el cargo).
   * Sin plazo, vence el mismo día de inicio.
   */
  saleCollectionDueDate(sale: SaleDTO): Date | null {
    return this.collectionDueDate(parseApiCalendarDate(sale.collectionStartDate)?.toJSDate() ?? null, sale.daysUntilExpiry);
  }

  collectionDueDate(start: Date | null, daysUntilExpiry: number | null | undefined): Date | null {
    if (!start) return null;
    const days = daysUntilExpiry != null && Number.isFinite(Number(daysUntilExpiry))
      ? Math.trunc(Number(daysUntilExpiry))
      : 0;
    const due = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    due.setDate(due.getDate() + days);
    return due;
  }

  /** El vencimiento ya pasó si la fecha es anterior al día de hoy. */
  isDueDateExpired(due: Date | null): boolean {
    return this.daysOverdue(due) > 0;
  }

  /** Días completos desde el vencimiento hasta hoy. Cero si aún no vence. */
  daysOverdue(due: Date | null): number {
    if (!due) return 0;
    const today = new Date();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.round((todayDay.getTime() - dueDay.getTime()) / 86_400_000);
    return days > 0 ? days : 0;
  }

  overdueTooltip(due: Date | null): string {
    const days = this.daysOverdue(due);
    if (days <= 0) return '';
    return days === 1 ? 'Vencida hace 1 día' : `Vencida hace ${days} días`;
  }

  saleCollectionValidity(sale: SaleDTO) {
    return this.collectionValidity(
      parseApiCalendarDate(sale.collectionStartDate)?.toJSDate() ?? null,
      this.saleCollectionDueDate(sale)
    );
  }

  /** Avance de la vigencia del cobro: verde 0–70 %, amarillo 71–99 %, rojo 100 %. */
  collectionValidity(start: Date | null, due: Date | null): {
    start: Date;
    due: Date;
    percent: number;
    barClass: string;
    expired: boolean;
    remainingLabel: string;
  } | null {
    if (!start || !due) return null;
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const span = dueDay - startDay;
    let percent: number;
    if (today >= dueDay) {
      percent = 100;
    } else if (today <= startDay || span <= 0) {
      percent = 0;
    } else {
      percent = Math.min(99, Math.floor(((today - startDay) / span) * 100));
    }
    const barClass = percent >= 100 ? 'bg-red-500' : percent > 70 ? 'bg-amber-400' : 'bg-emerald-500';
    const daysLeft = Math.round((dueDay - today) / 86_400_000);
    const absDays = Math.abs(daysLeft);
    const daysText = absDays === 1 ? '1 día' : `${absDays} días`;
    const remainingLabel = daysLeft < 0
      ? `Vencido hace ${daysText}`
      : daysLeft === 0
        ? 'Vence hoy'
        : `Vence en ${daysText}`;
    return { start, due, percent, barClass, expired: this.isDueDateExpired(due), remainingLabel };
  }

  /** Diálogo adelantar ciclos de facturación. */
  advanceCyclesDialogVisible = signal(false);
  advanceCyclesConfirmVisible = signal(false);
  advanceCycles = signal(1);
  advanceGroupCycles = signal(false);
  advancePreview = signal<AdvanceBillingCyclesResultDTO | null>(null);
  advancePreviewLoading = signal(false);
  advanceSubmitting = signal(false);
  private advancePreviewTimer: ReturnType<typeof setTimeout> | null = null;

  /** Confirmación al crear suscripción con ciclos retrasados. */
  catchUpConfirmVisible = signal(false);
  private pendingSubmitStatus: 'DRAFT' | 'ISSUED' = 'ISSUED';

  /** Elección de modo al cambiar frecuencia o día de cobro de una suscripción con ciclo activo. */
  billingCycleChangeVisible = signal(false);
  billingCycleChangeSelection = signal<BillingCycleChangeModeValue | null>(null);
  private pendingBillingCycleChangeMode: BillingCycleChangeModeValue | undefined;

  private saleStatusOptions: { value: InvoiceStatusEnumValue; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ISSUED', label: 'Emitida' },
    { value: 'PARTIALLY_PAID', label: 'Parcialmente pagada' },
    { value: 'PAID', label: 'Pagada' },
    { value: 'CANCELLED', label: 'Cancelada' },
    { value: 'VOID', label: 'Anulada' },
    { value: 'UNCOLLECTIBLE', label: 'Incobrable' }
  ];

  private salePipelineStatusOptions: { value: SalePipelineStatusEnumValue; label: string }[] = [
    { value: 'ACCEPTED', label: 'Aceptado' },
    { value: 'CONTRACT_ATTACHMENT', label: 'Contrato/Anexo' },
    { value: 'OC', label: 'OC' },
    { value: 'INVOICED', label: 'Facturado' },
    { value: 'INVOICE_IMPORTED', label: 'Factura Ingresada' },
    { value: 'PAID', label: 'Pagado' },
    { value: 'LOST', label: 'Perdido' }
  ];

  /** Sin tipo FISCAL en este formulario: asteriscos de obligatoriedad CFDI desactivados. */
  isFiscalBillingType = computed(() => false);

  /** CFDI ya timbrado: sección Facturación en solo lectura. */
  isInvoicingProfileStampedLocked = computed(() => this.currentSale()?.isStamped === true);

  /**
   * Venta en estado cerrado operativo (misma condición que timbrar CFDI vía barra).
   * Permite editar método/forma de pago y uso CFDI si aún no está timbrada.
   */
  saleIsClosedForInvoicingProfile = computed(() => {
    const s = this.currentSale()?.status;
    return (
      s === InvoiceStatusEnum.ISSUED ||
      s === InvoiceStatusEnum.PARTIALLY_PAID ||
      s === InvoiceStatusEnum.PAID
    );
  });

  /** Método de pago y uso de CFDI editables según borrador / venta cerrada sin timbrar. */
  canEditInvoicingPaymentAndCfdiFields = computed(() => {
    if (this.isInvoicingProfileStampedLocked()) return false;
    const sale = this.currentSale();
    if (!sale) return true;
    if (sale.status === InvoiceStatusEnum.DRAFT) return true;
    return this.saleIsClosedForInvoicingProfile();
  });

  /** Pie: botón PUT /sales/{id}/invoicing en tab General cuando la venta está cerrada sin timbrar. */
  showInvoicingProfileUpdateInFooter = computed(
    () =>
      this.activeTab() === 'general' &&
      this.isEditMode() &&
      this.saleIsClosedForInvoicingProfile() &&
      !this.isInvoicingProfileStampedLocked()
  );

  /** Cuando hay venta asociada cerrada: se ocultan Guardar / Crear en el pie. */
  saleIsClosedOperationalFooter = computed(
    () =>
      this.isEditMode() &&
      this.currentSale() != null &&
      this.currentSale()!.status !== InvoiceStatusEnum.DRAFT
  );

  /** Términos de pago por defecto de la empresa (GET /companies/me). */
  private companyPaymentTerms: { daysUntilExpiry: number | null } | null = null;

  /** Embudo comercial — API `processStatus` (`SaleProcessStatusEnum`). Valor fijo OC (no editable en este formulario). */
  private readonly defaultProcessStatus = SalePipelineStatusEnum.OC;

  /** Estado operativo — API `status` (`SaleProcessStatus`). */
  saleProcessStatusOptions: { value: (typeof SaleProcessStatusEnum)[keyof typeof SaleProcessStatusEnum]; label: string }[] = [
    { value: SaleProcessStatusEnum.DRAFT, label: 'Borrador' },
    { value: SaleProcessStatusEnum.QUOTATION, label: 'Cotización' },
    { value: SaleProcessStatusEnum.OC, label: 'Orden de compra' },
    { value: SaleProcessStatusEnum.IN_BILLING, label: 'En facturación' },
    { value: SaleProcessStatusEnum.BILLED, label: 'Facturado' }
  ];

  /** Ayuda del estado de la suscripción (cabecera, solo lectura; API `state`). */
  readonly statusTooltip = 'Estado de la suscripción en el sistema: activa o inactiva.';

  isSubscriptionInactive = computed(() => {
    const sub = this.currentSubscription() ?? this.currentSale()?.subscription ?? null;
    return sub?.state === EntityState.INACTIVE;
  });

  /**
   * Catálogo completo de métodos de cobro (orden: liga, cargo recurrente, transferencia, manual).
   * El select filtra según tipo de venta; las etiquetas de cargos usan la lista completa.
   */
  private readonly allCollectionMethodOptions: { value: CollectionMethod; label: string }[] = [
    { value: CollectionMethodEnum.LINK, label: 'Liga de pago' },
    { value: CollectionMethodEnum.RECURRING_CHARGE, label: 'Cobro recurrente' },
    { value: CollectionMethodEnum.MANUAL, label: 'Manual' },
    { value: CollectionMethodEnum.MERCHANT_INITIATED, label: 'Cobro bajo demanda' }
  ];

  /**
   * Venta única: liga, transferencia, manual.
   * Suscripción: liga, cargo recurrente, transferencia, manual.
   */
  collectionMethodOptions(): { value: CollectionMethod; label: string }[] {
    const selectable = this.allCollectionMethodOptions.filter(
      (o) => o.value !== CollectionMethodEnum.MERCHANT_INITIATED
    );
    let options = this.isSubscriptionSaleType()
      ? selectable
      : selectable.filter(
          (o) => o.value !== CollectionMethodEnum.RECURRING_CHARGE
        );

    if (this.isExistingSubscriptionEdit() && !this.isCollectionMethodLocked()) {
      options = options.filter((o) => o.value !== CollectionMethodEnum.RECURRING_CHARGE);
    }

    return options;
  }

  /** Etiqueta legible del método de cobro en la tabla de cargos (mismas opciones que el formulario). */
  getChargeCollectionMethodLabel(method: string | undefined): string {
    if (method == null || String(method).trim() === '') return '-';
    return this.allCollectionMethodOptions.find((o) => o.value === method)?.label ?? method;
  }

  /** Etiqueta del método de pago del intento (tarjeta, SPEI, etc.). */
  getPaymentAttemptMethodLabel(method: PaymentRecordMethodEnumValue | string | undefined): string {
    if (method == null || String(method).trim() === '') return '-';
    const labels: Record<string, string> = {
      [PaymentRecordMethodEnum.CREDIT_CARD]: 'Tarjeta de crédito',
      [PaymentRecordMethodEnum.DEBIT_CARD]: 'Tarjeta de débito',
      [PaymentRecordMethodEnum.CASH]: 'Efectivo',
      [PaymentRecordMethodEnum.TRANSFER]: 'Transferencia',
      [PaymentRecordMethodEnum.CHECK]: 'Cheque',
      [PaymentRecordMethodEnum.SPEI]: 'SPEI',
      [PaymentRecordMethodEnum.COD]: 'Contra entrega',
      [PaymentRecordMethodEnum.OTHER]: 'Otro'
    };
    return labels[method] ?? method;
  }

  /** Etiqueta del status del intento de pago. */
  getPaymentAttemptStatusLabel(status: PaymentRecordStatusEnumValue | string | undefined): string {
    if (status == null || String(status).trim() === '') return '-';
    const labels: Record<string, string> = {
      [PaymentRecordStatusEnum.PAID]: 'Pagado',
      [PaymentRecordStatusEnum.PENDING]: 'Pendiente',
      [PaymentRecordStatusEnum.FAILED]: 'Fallido',
      [PaymentRecordStatusEnum.CANCELLED]: 'Cancelado',
      [PaymentRecordStatusEnum.EXPIRED]: 'Expirado'
    };
    return labels[status] ?? status;
  }

  frequencyOptions: { value: string; label: string }[] = [
    { value: 'ONCE', label: 'Una sola vez' },
    { value: 'DAILY', label: 'Diario' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'BIWEEKLY', label: 'Quincenal' },
    { value: 'MONTHLY', label: 'Mensual' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'SEMIANNUALLY', label: 'Semestral' },
    { value: 'ANNUALLY', label: 'Anual' }
  ];

  /** Opciones de frecuencia para el select. En suscripción no se ofrece «Una sola vez». */
  frequencySelectOptions(): { value: string; label: string; disabled: boolean }[] {
    const opts = this.isSubscriptionSaleType()
      ? this.frequencyOptions.filter((opt) => opt.value !== 'ONCE')
      : this.frequencyOptions;
    return opts.map((opt) => ({ ...opt, disabled: false }));
  }

  /** Este formulario es solo de suscripción. */
  isSubscriptionSaleType(): boolean {
    return true;
  }

  taxCollectionTypeOptions: { value: string; label: string }[] = [
    { value: 'SELLER_COLLECTS', label: 'Traslado' },
    { value: 'SELLER_WITHHOLDS', label: 'Retención' }
  ];

  taxApplicationOptions: { value: TaxApplicationType; label: string }[] = [
    { value: 'TAXABLE', label: 'Gravado' },
    { value: 'EXEMPT', label: 'Exento' },
    { value: 'ZERO_RATED', label: 'Tasa 0%' },
    { value: 'REVERSE_CHARGE', label: 'Inversión del sujeto' }
  ];

  taxExemptOptions = [
    { value: false, label: 'No' },
    { value: true, label: 'Sí' }
  ];

  taxInclusiveHelpText =
    'Indica si el precio unitario ya incluye impuestos. Si está marcado, el importe mostrado ya considera los impuestos y no se sumarán impuestos adicionales sobre el precio.';

  currencySelectOptions(): { id: number; label: string }[] {
    return this.currencyOptions().map((c) => ({
      id: c.id!,
      label: `${c.code} - ${c.name}`
    }));
  }

  /** Código de moneda seleccionada para formatear totales. */
  get currencyCode(): string {
    const id = this.generalForm.get('currencyId')?.value ?? this.currentSale()?.currency;
    const currency = this.currencyOptions().find((c) => c.id === id);
    if (currency?.code) return currency.code;
    if (typeof this.currentSale()?.currency === 'string') return this.currentSale()!.currency!;
    return 'MXN';
  }

  totals = computed(() => {
    const items = this.lines();
    let subtotal = 0;
    let traslados = 0;
    let retenciones = 0;
    items.forEach((l) => {
      subtotal += Number(l.subtotal) || 0;
      (l.taxes ?? []).forEach((t) => {
        const amount = Number(t.taxAmount) || 0;
        if (t.collectionType === 'SELLER_WITHHOLDS') {
          retenciones += amount;
        } else {
          traslados += amount;
        }
      });
    });
    const tax = traslados - retenciones;
    const total = subtotal + tax;
    return { subtotal, tax, total, traslados, retenciones };
  });

  constructor() {
    this.generalForm = this.fb.group({
      customerId: [null, Validators.required],
      customerSelection: [null],
      planId: [null],
      planSelection: [null],
      number: ['', [Validators.maxLength(20)]],
      subtotal: [0, [Validators.required, Validators.min(0)]],
      traslados: [0, [Validators.min(0)]],
      retenciones: [0, [Validators.min(0)]],
      tax: [0, [Validators.required, Validators.min(0)]],
      discount: [0, [Validators.required, Validators.min(0)]],
      total: [0, [Validators.required, Validators.min(0)]],
      currencyId: [null, Validators.required],
      processStatus: [SalePipelineStatusEnum.OC, Validators.required],
      status: [SaleProcessStatusEnum.DRAFT, Validators.required],
      collectionMethod: [null, Validators.required],
      notes: ['', [Validators.maxLength(500)]]
    });

    merge(this.generalForm.valueChanges, this.generalForm.statusChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.generalTabDraftRefresh.update((n) => n + 1));

    this.generalForm
      .get('discount')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isExistingSubscriptionEdit()) {
          this.markSubscriptionBillingDirty();
        }
      });

    this.invoicingForm = this.fb.group({
      paymentFormId: [null],
      paymentMethodId: [null],
      cfdiUseId: [null],
      autoInvoice: [false]
    });

    this.paymentTermsForm = this.fb.group({
      collectionStartDate: [null as Date | null],
      billingDayOfMonth: [null as number | null, [Validators.min(1), Validators.max(31)]],
      daysUntilExpiry: [null],
      penaltyFeeAmount: [null],
      penaltyInterestRate: [null],
      frequency: ['MONTHLY', [Validators.required]]
    });

    this.additionalFieldsForm = this.fb.group({});

    this.saleForm = this.fb.group({
      general: this.generalForm,
      invoicing: this.invoicingForm,
      paymentTerms: this.paymentTermsForm,
      additionalFields: this.additionalFieldsForm
    });

    merge(this.additionalFieldsForm.valueChanges, this.additionalFieldsForm.statusChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.additionalFieldsRefresh.update((n) => n + 1));

    this.paymentTermsForm
      .get('frequency')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncBillingDayOfMonthControl());

    merge(this.paymentTermsForm.statusChanges, this.paymentTermsForm.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.paymentTermsDraftRefresh.update((n) => n + 1);
        this.refreshCatchUpPreview();
      });

    this.refreshCatchUpPreview();

    merge(
      this.invoicingForm.statusChanges,
      this.invoicingForm.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.paymentTermsDraftRefresh.update((n) => n + 1));

    merge(
      this.invoicingForm.get('paymentMethodId')!.valueChanges,
      this.invoicingForm.get('paymentFormId')!.valueChanges,
      this.invoicingForm.get('cfdiUseId')!.valueChanges,
      this.invoicingForm.get('autoInvoice')!.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());

    this.invoicingForm
      .get('autoInvoice')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncInvoicingControlsEnabledState());

    this.syncSubscriptionPaymentTermsFromSaleType();

    this.syncInvoicingControlsEnabledState();

    this.lineForm = this.fb.group({
      productSelection: [null],
      productId: [null],
      description: ['', [Validators.required, Validators.maxLength(250)]],
      quantity: [1, [Validators.required, Validators.min(0.0001)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      measureUnitSelection: [null],
      measureUnitId: [null],
      taxProductCodeSelection: [null],
      taxProductCodeId: [null],
      satObjetoImpSelection: [null],
      satObjetoImpId: [null],
      taxExempt: [false],
      taxInclusive: [false],
      measureUnitSearch: [''],
      taxProductCodeSearch: ['']
    });

    merge(
      this.lineForm.get('quantity')!.valueChanges,
      this.lineForm.get('unitPrice')!.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshLineSubtotal());
    this.refreshLineSubtotal();

    this.lineTaxForm = this.fb.group({
      taxSelection: [null, Validators.required],
      taxCode: [''],
      taxRate: [null, [Validators.min(0), Validators.max(100)]],
      taxAmount: [null, [Validators.min(0)]],
      taxBase: ['', [Validators.min(0)]],
      applicationType: ['TAXABLE', Validators.required],
      collectionType: ['SELLER_COLLECTS']
    });
    this.setupLineTaxFormExclusiveFields();

    this.generalForm.get('number')?.disable({ emitEvent: false });

    this.loadCurrencies();
    this.loadTaxes();
    this.loadPaymentForms();
    this.loadPaymentMethods();
    this.loadSATCFDIUses();
    this.loadCompanyPaymentTerms();

    this.syncSubscriptionPaymentTermsFromSaleType();

    this.route.paramMap.subscribe((params) => {
      const routeId = params.get('id');
      if (routeId && routeId !== 'nuevo') {
        const id = Number(routeId);
        if (!Number.isNaN(id)) {
          this.isEditMode.set(true);
          this.syncCustomerLockState();
          this.editingSubscriptionId.set(id);
          this.editingSaleId.set(null);
          this.subscriptionSales.set([]);
          this.salesLoaded = false;
          this.loadSubscription(id);
          return;
        }
      }
      this.isEditMode.set(false);
      this.syncCustomerLockState();
      this.editingSubscriptionId.set(null);
      this.editingSaleId.set(null);
      this.subscriptionSales.set([]);
      this.salesLoaded = false;
      this.currentSale.set(null);
      this.currentSubscription.set(null);
      this.savedSubscriptionHash.set(null);
      this.currentSubscriptionHash.set(null);
      this.subscriptionBillingDirty.set(false);
      this.selectedPlan.set(null);
      this.planOptions.set([]);
      this.lines.set([]);
      this.generalForm.patchValue(
        {
          subtotal: 0,
          tax: 0,
          traslados: 0,
          retenciones: 0,
          discount: 0,
          total: 0,
          number: '',
          currencyId: null,
          planId: null,
          planSelection: null,
          processStatus: SalePipelineStatusEnum.OC,
          status: SaleProcessStatusEnum.DRAFT,
          collectionMethod: null
        },
        { emitEvent: false }
      );
      this.applyDefaultCurrencyIfNeeded();
      this.invoicingForm.reset({
        paymentFormId: null,
        paymentMethodId: null,
        cfdiUseId: null,
        autoInvoice: false
      });
      this.syncInvoicingControlsEnabledState();
      this.paymentTermsForm.patchValue({
        collectionStartDate: null,
        billingDayOfMonth: null,
        daysUntilExpiry: null,
        penaltyFeeAmount: null,
        penaltyInterestRate: null,
        frequency: 'MONTHLY'
      });
      this.groupCatchUpCycles.set(false);
      this.syncSubscriptionPaymentTermsFromSaleType();
      this.syncDefaultPaymentMethod();
      this.activeTab.set('general');
      this.generalTabDraftRefresh.update((n) => n + 1);
      this.refreshCatchUpPreview();
      this.cdr.markForCheck();
    });

    effect(() => {
      this.isEditMode();
      this.refreshCatchUpPreview();
    });

    effect(() => {
      this.currentSale()?.status;
      this.currentSale()?.isStamped;
      this.syncInvoicingControlsEnabledState();
    });

    effect(() => {
      this.isReadonly();
      this.syncAdditionalFieldsEnabledState();
    });

    effect(() => {
      this.isExistingSubscriptionEdit();
      this.isReadonly();
      this.currentSubscription()?.collectionMethod;
      this.syncSubscriptionImmutableFieldsLockState();
    });
  }

  /**
   * Forma de pago, método de pago y uso CFDI según borrador, venta cerrada sin timbrar
   * o bloqueo por CFDI. Con autoInvoice, método de pago y uso de CFDI son obligatorios.
   */
  private syncInvoicingControlsEnabledState(): void {
    const autoInvoiceRequired = !!this.invoicingForm.get('autoInvoice')?.value;
    const canEdit = this.canEditInvoicingPaymentAndCfdiFields();

    const methodCtrl = this.invoicingForm.get('paymentMethodId');
    if (methodCtrl) {
      methodCtrl.clearValidators();
      if (autoInvoiceRequired) {
        methodCtrl.setValidators([Validators.required]);
      }
      if (canEdit) {
        methodCtrl.enable({ emitEvent: false });
      } else {
        methodCtrl.disable({ emitEvent: false });
      }
      methodCtrl.updateValueAndValidity({ emitEvent: false });
    }

    const formCtrl = this.invoicingForm.get('paymentFormId');
    if (formCtrl) {
      formCtrl.clearValidators();
      if (canEdit) {
        formCtrl.enable({ emitEvent: false });
      } else {
        formCtrl.disable({ emitEvent: false });
      }
      formCtrl.updateValueAndValidity({ emitEvent: false });
    }

    const cfdiCtrl = this.invoicingForm.get('cfdiUseId');
    if (cfdiCtrl) {
      cfdiCtrl.clearValidators();
      if (autoInvoiceRequired) {
        cfdiCtrl.setValidators([Validators.required]);
      }
      if (canEdit) {
        cfdiCtrl.enable({ emitEvent: false });
      } else {
        cfdiCtrl.disable({ emitEvent: false });
      }
      cfdiCtrl.updateValueAndValidity({ emitEvent: false });
    }

    const autoInvoiceCtrl = this.invoicingForm.get('autoInvoice');
    if (autoInvoiceCtrl) {
      if (canEdit) {
        autoInvoiceCtrl.enable({ emitEvent: false });
      } else {
        autoInvoiceCtrl.disable({ emitEvent: false });
      }
    }
    this.cdr.markForCheck();
  }

  /** Asteriscos de obligatoriedad cuando autoInvoice está marcado. */
  requiresAutoInvoiceFiscalFields(): boolean {
    return !!this.invoicingForm?.get('autoInvoice')?.value;
  }

  /** Aviso cuando autoInvoice está activo y al cliente le faltan datos para timbrar. */
  autoInvoiceCustomerWarning(): string | null {
    if (!this.requiresAutoInvoiceFiscalFields()) {
      return null;
    }
    const customerId = this.generalForm.get('customerId')?.value as number | null;
    if (customerId == null) {
      return null;
    }
    if (this.invoiceCustomerStatus() === 'loading') {
      return 'Verificando los datos fiscales del cliente…';
    }
    const missing = missingCustomerInvoiceFields(this.invoiceCustomer());
    return missing.length ? customerInvoiceReadinessMessage(missing) : null;
  }

  private loadInvoiceCustomer(customerId: number | null | undefined): void {
    if (customerId == null) {
      this.invoiceCustomer.set(null);
      this.invoiceCustomerStatus.set('empty');
      return;
    }
    this.invoiceCustomerStatus.set('loading');
    this.customerService
      .getCustomerById(customerId)
      .pipe(catchError(() => of({ ok: false, body: null as CustomerDTO | null })))
      .subscribe((response) => {
        if (this.generalForm.get('customerId')?.value !== customerId) {
          return;
        }
        this.invoiceCustomer.set(response.ok && response.body ? response.body : null);
        this.invoiceCustomerStatus.set('ready');
        this.cdr.markForCheck();
      });
  }

  /**
   * Auto-selecciona método de pago SAT PUE si aún no hay uno elegido.
   */
  private syncDefaultPaymentMethod(): void {
    if (this.invoicingForm.get('paymentMethodId')?.value != null) return;
    const match = this.paymentMethodOptions().find((m) => m.code === 'PUE');
    if (match?.id == null) return;
    this.invoicingForm.patchValue({ paymentMethodId: match.id }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  /** Dirty solo en los controles que envía PUT /sales/{id}/invoicing. */
  invoicingProfileSubsetDirty(): boolean {
    return !!(
      this.invoicingForm.get('paymentMethodId')?.dirty ||
      this.invoicingForm.get('paymentFormId')?.dirty ||
      this.invoicingForm.get('cfdiUseId')?.dirty ||
      this.invoicingForm.get('autoInvoice')?.dirty
    );
  }

  submitInvoicingProfileUpdate(): void {
    const id = this.editingSaleId();
    if (id == null || this.invoicingProfileUpdateLoading()) return;
    const autoInvoiceWarning = this.autoInvoiceCustomerWarning();
    if (autoInvoiceWarning) {
      this.toastr.error(autoInvoiceWarning);
      return;
    }
    const raw = this.invoicingForm.getRawValue();
    this.invoicingProfileUpdateLoading.set(true);
    this.saleService
      .updateInvoicingProfile(id, {
        paymentFormId: raw.paymentFormId ?? null,
        paymentMethodId: raw.paymentMethodId ?? null,
        cfdiUseId: raw.cfdiUseId ?? null,
        autoInvoice: !!raw.autoInvoice
      })
      .pipe(finalize(() => this.invoicingProfileUpdateLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.currentSale.set(r.body);
            this.patchSale(r.body);
            this.invoicingForm.markAsPristine();
            this.toastr.success('Perfil de facturación actualizado');
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo actualizar el perfil de facturación'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al actualizar el perfil de facturación'));
        }
      });
  }

  /**
   * Suscripción: frecuencia obligatoria y habilitada (salvo plan/readonly).
   */
  private syncSubscriptionPaymentTermsFromSaleType(): void {
    const freqCtrl = this.paymentTermsForm.get('frequency');
    if (!freqCtrl) return;

    freqCtrl.enable({ emitEvent: false });
    freqCtrl.setValidators([Validators.required]);
    if (freqCtrl.value === 'ONCE' || freqCtrl.value == null || freqCtrl.value === '') {
      freqCtrl.patchValue('MONTHLY', { emitEvent: false });
    }
    freqCtrl.updateValueAndValidity({ emitEvent: false });
    if (this.selectedPlan() != null || this.isReadonly()) {
      freqCtrl.disable({ emitEvent: false });
    }
    this.syncBillingDayOfMonthControl();
    this.syncDefaultPaymentMethod();
    this.cdr.markForCheck();
  }

  /** Folio mostrado cuando la suscripción ya tiene id. La etiqueta «Autogenerado» va solo en la plantilla si id es null. */
  folioDisplayValue(): string {
    if (this.loading() && this.editingSubscriptionId() != null) {
      return '…';
    }
    const fromApi = this.currentSubscription()?.number?.trim();
    if (fromApi) return fromApi;
    const fromForm = this.generalForm.getRawValue().number?.trim();
    return fromForm || '—';
  }

  /** Etiqueta legible del estado mostrado en el badge STATUS. */
  statusDisplayLabel(): string {
    if (this.loading() && (this.editingSubscriptionId() != null || this.editingSaleId() != null)) {
      return '…';
    }
    if (this.isSubscriptionCancelled()) {
      return 'Cancelada';
    }
    const raw = this.resolvedSubscriptionState();
    if (raw == null) {
      return this.editingSubscriptionId() == null ? 'Activo' : '—';
    }
    const labels: Record<string, string> = {
      [EntityState.ACTIVE]: 'Activo',
      [EntityState.INACTIVE]: 'Inactivo'
    };
    return labels[raw] ?? raw;
  }

  /** True cuando la suscripción está cancelada (`CANCELED` / legacy `CANCELLED`). */
  isSubscriptionCancelled(): boolean {
    const status =
      this.currentSubscription()?.status ??
      this.currentSale()?.subscription?.status ??
      null;
    return (
      status === SubscriptionStatusEnum.CANCELED ||
      status === SubscriptionStatusEnum.CANCELLED
    );
  }

  /** Clases de color del badge STATUS según `state`. */
  statusDisplayClasses(): string {
    const raw =
      this.resolvedSubscriptionState() ??
      (this.editingSubscriptionId() == null ? EntityState.ACTIVE : null);
    const base =
      'inline-flex w-fit max-w-xl shrink-0 flex-wrap items-baseline gap-x-2 gap-y-1 self-end rounded-xl border px-4 py-3 shadow-sm sm:self-auto';
    switch (raw) {
      case EntityState.ACTIVE:
        return `${base} border-emerald-200 bg-emerald-50`;
      case EntityState.INACTIVE:
        return `${base} border-red-200 bg-red-50`;
      default:
        return `${base} border-slate-200 bg-slate-50`;
    }
  }

  statusDisplayLabelClasses(): string {
    const raw =
      this.resolvedSubscriptionState() ??
      (this.editingSubscriptionId() == null ? EntityState.ACTIVE : null);
    const base = 'text-base font-medium font-mono';
    switch (raw) {
      case EntityState.ACTIVE:
        return `${base} text-emerald-800`;
      case EntityState.INACTIVE:
        return `${base} text-red-800`;
      default:
        return `${base} text-slate-900`;
    }
  }

  statusDisplayCaptionClasses(): string {
    const raw =
      this.resolvedSubscriptionState() ??
      (this.editingSubscriptionId() == null ? EntityState.ACTIVE : null);
    const base = 'text-xs font-semibold tracking-wider';
    switch (raw) {
      case EntityState.ACTIVE:
        return `${base} text-emerald-600`;
      case EntityState.INACTIVE:
        return `${base} text-red-600`;
      default:
        return `${base} text-slate-500`;
    }
  }

  private resolvedSubscriptionState(): string | null {
    if (this.loading() && (this.editingSubscriptionId() != null || this.editingSaleId() != null)) {
      return null;
    }
    const raw =
      this.currentSubscription()?.state ??
      this.currentSale()?.subscription?.state ??
      null;
    if (raw == null || String(raw).trim() === '') return null;
    return String(raw);
  }

  private effectTotals = effect(() => {
    const t = this.totals();
    this.generalForm.patchValue(
      {
        subtotal: t.subtotal,
        tax: t.tax,
        traslados: t.traslados,
        retenciones: t.retenciones,
        discount: this.generalForm.get('discount')?.value ?? 0,
        total: t.total
      },
      { emitEvent: false }
    );
  });

  private billingHashEffectSeq = 0;

  private effectSubscriptionBillingHash = effect(() => {
    if (!this.subscriptionBillingDirty()) {
      return;
    }

    this.lines();
    this.totals();
    this.generalTabDraftRefresh();

    if (!this.isExistingSubscriptionEdit() || this.isPlanLocked()) {
      return;
    }

    const seq = ++this.billingHashEffectSeq;
    void this.computeSubscriptionBillingHashFromForm().then((hash) => {
      if (seq !== this.billingHashEffectSeq) return;
      this.currentSubscriptionHash.set(hash);
      this.cdr.markForCheck();
    });
  });

  private markSubscriptionBillingDirty(): void {
    if (!this.isExistingSubscriptionEdit() || this.isPlanLocked() || this.isReadonly()) {
      return;
    }
    this.subscriptionBillingDirty.set(true);
  }

  onCustomerSearch(event: { query: string }): void {
    if (this.isCustomerLocked()) {
      this.customerOptions.set([]);
      return;
    }
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.customerOptions.set([]);
      return;
    }
    this.customerLoading.set(true);
    this.customerService
      .listCustomers(
        { 'legalName[lk]': q, 'comercialName[lk]': q },
        { page: 0, size: 20 }
      )
      .pipe(
        catchError(() => of({ ok: false, body: [] as CustomerDTO[] })),
        finalize(() => this.customerLoading.set(false))
      )
      .subscribe((r) => {
        const list = r.ok && r.body ? r.body : [];
        this.customerOptions.set(list.map((c) => this.customerWithDisplayLabel(c)));
      });
  }

  onPlanSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.planOptions.set([]);
      return;
    }
    this.planService
      .searchPlans({ 'name[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as PlanDTO[] })))
      .subscribe((r) => this.planOptions.set(r.ok && r.body ? (r.body as PlanDTO[]) : []));
  }

  onPlanSelected(plan: PlanDTO | null): void {
    if (this.isExistingSubscriptionEdit()) return;
    if (!plan?.id) return;
    this.generalForm.patchValue({
      planId: plan.id,
      planSelection: plan
    });
    this.selectedPlan.set(plan);
    // Cargar detalle completo (items) para autocompletar líneas.
    this.planService.getPlanById(plan.id).subscribe({
      next: (r) => {
        if (r.ok && r.body) {
          this.applyPlanToForm(r.body);
        } else {
          this.applyPlanToForm(plan);
        }
      },
      error: () => this.applyPlanToForm(plan)
    });
  }

  onPlanClear(): void {
    if (this.isExistingSubscriptionEdit()) return;
    this.selectedPlan.set(null);
    this.generalForm.patchValue({
      planId: null,
      planSelection: null
    });
    this.syncPlanLockedCommercialFields();
    this.cdr.markForCheck();
  }

  getFrequencyLabel(freq: string | undefined): string {
    if (!freq) return '—';
    return this.frequencyOptions.find((o) => o.value === freq)?.label ?? String(freq);
  }

  /** Día de renovación solo aplica a frecuencias mensual, trimestral, semestral y anual. */
  private billingDayOfMonthAppliesToFrequency(frequency: string | null | undefined): boolean {
    return (
      frequency === 'MONTHLY' ||
      frequency === 'QUARTERLY' ||
      frequency === 'SEMIANNUALLY' ||
      frequency === 'ANNUALLY'
    );
  }

  private syncBillingDayOfMonthControl(): void {
    const ctrl = this.paymentTermsForm.get('billingDayOfMonth');
    if (!ctrl) return;

    const frequency = this.paymentTermsForm.get('frequency')?.value;
    const applies = this.billingDayOfMonthAppliesToFrequency(frequency);
    const shouldEnable = !this.isReadonly() && applies;

    if (shouldEnable) {
      if (ctrl.disabled) {
        ctrl.enable({ emitEvent: false });
      }
    } else {
      if (ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
      }
      if (!applies && !this.isReadonly() && ctrl.value != null) {
        ctrl.patchValue(null, { emitEvent: false });
      }
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /** Habilita/deshabilita moneda y frecuencia según haya plan seleccionado. */
  private syncPlanLockedCommercialFields(): void {
    const locked = this.selectedPlan() != null || this.isReadonly();
    const currencyCtrl = this.generalForm.get('currencyId');
    const frequencyCtrl = this.paymentTermsForm.get('frequency');

    if (locked) {
      currencyCtrl?.disable({ emitEvent: false });
      frequencyCtrl?.disable({ emitEvent: false });
    } else {
      currencyCtrl?.enable({ emitEvent: false });
      // En suscripción la frecuencia debe poder editarse sin plan.
      frequencyCtrl?.enable({ emitEvent: false });
      frequencyCtrl?.setValidators([Validators.required]);
      frequencyCtrl?.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** Copia frequency, moneda, montos, notas e items del plan a la suscripción. */
  private applyPlanToForm(plan: PlanDTO): void {
    this.selectedPlan.set(plan);
    this.generalForm.patchValue({
      planId: plan.id ?? null,
      planSelection: plan,
      subtotal: Number(plan.subtotal ?? 0),
      tax: Number(plan.tax ?? 0),
      discount: Number(plan.discount ?? 0),
      total: Number(plan.total ?? 0),
      notes: plan.description ?? this.generalForm.get('notes')?.value ?? ''
    }, { emitEvent: false });

    if (plan.currency) {
      const cur = this.currencyOptions().find((c) => c.code === plan.currency);
      if (cur?.id != null) {
        this.generalForm.patchValue({ currencyId: cur.id }, { emitEvent: false });
      }
    }

    const frequency = plan.frequency && plan.frequency !== 'ONCE' ? plan.frequency : 'MONTHLY';
    this.paymentTermsForm.patchValue({ frequency }, { emitEvent: false });
    this.syncSubscriptionPaymentTermsFromSaleType();
    this.syncPlanLockedCommercialFields();

    if (plan.items?.length) {
      this.lines.set(plan.items.map((item) => this.planItemToVm(item)));
    }

    this.generalTabDraftRefresh.update((n) => n + 1);
    this.cdr.markForCheck();
  }

  private planItemToVm(item: PlanItemDTO): SaleLineVm {
    return this.lineToVm(item as unknown as SaleLineDTO);
  }

  onProductSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.productOptions.set([]);
      return;
    }
    this.productLoading.set(true);
    this.productService
      .getProducts({ 'name[lk]': q }, { page: 0, size: 20 })
      .pipe(
        catchError(() => of({ ok: false, body: [] as ProductDTO[] })),
        finalize(() => this.productLoading.set(false))
      )
      .subscribe((r) => {
        this.productOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  onMeasureUnitSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.measureUnitOptions.set([]);
      return;
    }
    this.measureUnitService
      .listMeasureUnits({ 'name[lk]': q, 'code[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as MeasureUnitDTO[] })))
      .subscribe((r) => {
        this.measureUnitOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  /** Subtotal del modal de línea: cantidad × precio unitario. */
  private refreshLineSubtotal(): void {
    const qty = Number(this.lineForm.get('quantity')?.value) || 0;
    const price = Number(this.lineForm.get('unitPrice')?.value) || 0;
    this.lineSubtotal.set(qty * price);
  }

  /**
   * Selecciona todo el texto al enfocar cantidad/precio (Tab o click).
   * El flag + mouseup evitan que el click reposicione el caret; setTimeout
   * cubre el caso en que el mouseup ya haya corrido en el input interno.
   */
  private suppressMouseUpSelectClear = false;

  selectAllOnFocus(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input?.select) {
      return;
    }
    this.suppressMouseUpSelectClear = true;
    input.select();
    setTimeout(() => input.select());
  }

  /** Evita que el mouseup del click deshaga la selección hecha en focus. */
  keepSelectionOnMouseUp(event: Event): void {
    if (!this.suppressMouseUpSelectClear) {
      return;
    }
    event.preventDefault();
    this.suppressMouseUpSelectClear = false;
  }

  onTaxProductCodeSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.taxProductCodeOptions.set([]);
      return;
    }
    this.taxProductCodeService
      .listTaxProductCodes({ 'taxCode[lk]': q, 'taxDescription[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxProductCodeDTO[] })))
      .subscribe((r) => {
        this.taxProductCodeOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  onMeasureUnitSelected(value: MeasureUnitDTO | null): void {
    if (value) {
      this.lineForm.patchValue({ measureUnitId: value.id ?? null, measureUnitSelection: value });
    }
  }

  onTaxProductCodeSelected(value: TaxProductCodeDTO | null): void {
    if (value) {
      this.lineForm.patchValue({ taxProductCodeId: value.id ?? null, taxProductCodeSelection: value });
    }
  }

  loadCurrencies(): void {
    this.currencyLoading.set(true);
    this.currencyService
      .listCurrencies({}, { page: 0, size: 200 })
      .pipe(
        catchError(() => of({ ok: false, body: [] as CurrencyDTO[] })),
        finalize(() => this.currencyLoading.set(false))
      )
      .subscribe((r) => {
        this.currencyOptions.set(r.ok ? r.body : []);
        this.applyDefaultCurrencyIfNeeded();
      });
  }

  /** Moneda por defecto en ventas nuevas: MXN (Peso mexicano). No aplica al editar una venta existente. */
  private applyDefaultCurrencyIfNeeded(): void {
    if (this.editingSaleId() != null) return;
    if (this.generalForm.get('currencyId')?.value != null) return;
    const mxn = this.currencyOptions().find((c) => String(c.code).toUpperCase() === 'MXN');
    if (mxn?.id == null) return;
    this.generalForm.patchValue({ currencyId: mxn.id }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  loadTaxes(): void {
    this.taxService
      .listTaxes({}, { page: 0, size: 200 })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxDTO[] })))
      .subscribe((r) => {
        this.taxOptions.set(r.ok ? r.body : []);
      });
  }

  loadPaymentForms(): void {
    this.paymentFormService
      .listPaymentForms({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as PaymentFormDTO[] })))
      .subscribe((r) => {
        this.paymentFormOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  loadPaymentMethods(): void {
    this.paymentMethodService
      .listPaymentMethods({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as PaymentMethodDTO[] })))
      .subscribe((r) => {
        this.paymentMethodOptions.set(r.ok && r.body ? r.body : []);
        this.syncDefaultPaymentMethod();
      });
  }

  loadSATCFDIUses(): void {
    this.satCfdiUseService
      .list({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as SATCFDIUseDTO[] })))
      .subscribe((r) => {
        this.satCfdiUseOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  private loadCompanyPaymentTerms(): void {
    this.companyService
      .getMyCompany()
      .pipe(catchError(() => of({ ok: false, body: null as CompanyDTO | null })))
      .subscribe((r) => {
        const body = r.body;
        if (r.ok !== false && body) {
          this.companyPaymentTerms = {
            daysUntilExpiry: body.daysUntilExpiry ?? null
          };
          this.applyDynamicFieldsFromCompany(
            body,
            this.currentSubscription()?.additionalFields ?? null
          );
          if (
            this.currentSubscription() == null &&
            this.currentSale() == null &&
            !this.isReadonly()
          ) {
            this.invoicingForm.patchValue(
              {
                autoInvoice:
                  body.companyConfig?.companyInvoiceConfig?.enableAutoInvoice ?? false
              },
              { emitEvent: false }
            );
          }
        }
        if (
          this.isEditMode() ||
          this.currentSale() != null ||
          this.currentSubscription() != null ||
          this.isReadonly()
        ) {
          return;
        }
        const sel = this.generalForm.get('customerSelection')?.value as CustomerDTO | null;
        if (sel?.id) {
          this.customerService
            .getCustomerById(sel.id)
            .pipe(catchError(() => of({ ok: false, body: sel })))
            .subscribe((resp) => {
              const cust = resp.ok && resp.body ? resp.body : sel;
              this.applyPaymentTermsDefaultsForCustomer(cust);
            });
        } else {
          this.applyCompanyPaymentTermsOnly();
        }
      });
  }

  private applyDynamicFieldsFromCompany(
    company: CompanyDTO,
    existingValues?: Record<string, unknown> | null
  ): void {
    const fields = sortDynamicFields(
      company.companyConfig?.companyInvoiceConfig?.companySubscriptionConfig?.dynamicFields
    );
    this.dynamicFields.set(fields);
    rebuildAdditionalFieldsForm(this.fb, this.additionalFieldsForm, fields, existingValues);
    this.syncAdditionalFieldsEnabledState();
    this.additionalFieldsRefresh.update((n) => n + 1);
    this.cdr.markForCheck();
  }

  /** Deshabilita campos adicionales cuando la suscripción no es editable (aspecto disabled). */
  private syncAdditionalFieldsEnabledState(): void {
    const disabled = this.isReadonly();
    Object.keys(this.additionalFieldsForm.controls).forEach((key) => {
      const ctrl = this.additionalFieldsForm.get(key);
      if (!ctrl) return;
      if (disabled && ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
      } else if (!disabled && ctrl.disabled) {
        ctrl.enable({ emitEvent: false });
      }
    });
  }

  additionalFieldCtrl(fieldKey: string | undefined) {
    return additionalFieldControl(this.additionalFieldsForm, fieldKey);
  }

  selectOptionsForField(field: DynamicField): { label: string; value: string }[] {
    return (field.options ?? []).map((o) => ({ label: o, value: o }));
  }

  fieldTypeLabel(type: DynamicFieldType | undefined): string {
    return dynamicFieldTypeLabel(type);
  }

  /** Rellena plazo y gracia solo con la empresa (factura nueva sin cliente aún). */
  private applyCompanyPaymentTermsOnly(): void {
    const comp = this.companyPaymentTerms;
    if (!comp) return;
    this.paymentTermsForm.patchValue(
      {
        daysUntilExpiry: comp.daysUntilExpiry ?? null
      },
      { emitEvent: false }
    );
    this.cdr.markForCheck();
  }

  /**
   * Prioridad por campo: cliente → empresa → vacío (null).
   */
  private pickPaymentTerm(
    customerVal: number | null | undefined,
    companyVal: number | null | undefined
  ): number | null {
    if (customerVal !== null && customerVal !== undefined) {
      return customerVal;
    }
    if (companyVal !== null && companyVal !== undefined) {
      return companyVal;
    }
    return null;
  }

  private applyPaymentTermsDefaultsForCustomer(customer: CustomerDTO): void {
    const comp = this.companyPaymentTerms;
    const days = this.pickPaymentTerm(customer.daysUntilExpiry, comp?.daysUntilExpiry ?? null);
    this.paymentTermsForm.patchValue({ daysUntilExpiry: days }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  /** Carga el catálogo SATObjetoImp para el select de líneas. */
  private loadSatObjetoImps(page: number = 0, acc: SATObjetoImpDTO[] = []): void {
    this.satObjetoImpService
      .list({}, { page, size: 200, sort: ['name,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as SATObjetoImpDTO[], page: undefined })),
        finalize(() => {})
      )
      .subscribe((response) => {
        if (!response.ok) {
          this.satObjetoImpOptions.set([]);
          return;
        }
        const merged = [...acc, ...(response.body ?? [])];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          this.loadSatObjetoImps(response.page.currentPage + 1, merged);
          return;
        }
        this.satObjetoImpOptions.set(merged);
      });
  }

  loadSale(id: number): void {
    this.loading.set(true);
    this.saleService.getSaleById(id).subscribe({
      next: (r) => {
        if (r.ok && r.body) {
          this.currentSale.set(r.body);
          this.patchSale(r.body);
          const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
          this.lines.set(lineVms);
        } else {
          this.setError(r.message || 'Error al cargar la venta');
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.setError('Error al cargar la venta.');
        this.loading.set(false);
      }
    });
  }

  loadSubscription(id: number): void {
    this.loading.set(true);
    this.formDataLoading.set(true);
    this.subscriptionService
      .getSubscriptionById(id)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.formDataLoading.set(false);
        })
      )
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.currentSubscription.set(r.body);
            this.resetSubscriptionBillingHashBaseline(r.body);
            this.patchSubscription(r.body);
            const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
            this.lines.set(lineVms);
          } else {
            this.setError(r.message || 'Error al cargar la suscripción');
          }
        },
        error: () => {
          this.setError('Error al cargar la suscripción.');
        }
      });
  }

  /** Recarga la suscripción desde el API (botón refresh del header). */
  refreshSubscription(): void {
    const id = this.editingSubscriptionId();
    if (id == null || this.loading()) return;
    this.loadSubscription(id);
    if (this.activeTab() === 'sales') this.loadSubscriptionSales();
  }

  private resetSubscriptionBillingHashBaseline(sub: SubscriptionDTO | null): void {
    const hash = sub?.hash ?? null;
    this.savedSubscriptionHash.set(hash);
    this.currentSubscriptionHash.set(hash);
    this.subscriptionBillingDirty.set(false);
  }

  private async computeSubscriptionBillingHashFromForm(): Promise<string | null> {
    if (!this.isExistingSubscriptionEdit() || this.isPlanLocked()) {
      return null;
    }

    const currencyId = this.generalForm.get('currencyId')?.value;
    if (currencyId == null) {
      return null;
    }

    const gv = this.generalForm.getRawValue();
    const subtotal = Number(gv.subtotal ?? 0);
    const tax = Math.max(0, Number(gv.tax ?? 0));
    const discount = Number(gv.discount ?? 0);
    const total = Math.max(0, Number(gv.total ?? 0));

    const persistedLines = [...this.lines()]
      .filter((line) => line.id != null)
      .sort((a, b) => a.id! - b.id!);
    const newLines = [...this.lines()].filter((line) => line.id == null);

    const lineHashes: string[] = [];
    for (const line of persistedLines) {
      lineHashes.push(
        await lineSha256Hash({
          productId: line.productId,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          taxes: line.taxes
        })
      );
    }
    for (const line of newLines.sort((a, b) => (a.tempId ?? '').localeCompare(b.tempId ?? ''))) {
      lineHashes.push(
        await lineSha256Hash({
          productId: line.productId,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          taxes: line.taxes
        })
      );
    }

    return subscriptionSha256Hash({
      currencyId: Number(currencyId),
      subtotal,
      tax,
      discount,
      total,
      lineHashes
    });
  }

  private patchSubscription(sub: SubscriptionDTO): void {
    this.generalForm.patchValue(
      {
        customerId: sub.customer?.id ?? null,
        customerSelection: sub.customer ? this.customerWithDisplayLabel(sub.customer) : null,
        planId: sub.plan?.id ?? null,
        planSelection: sub.plan ?? null,
        number: sub.number ?? '',
        processStatus: SalePipelineStatusEnum.OC,
        status: SaleProcessStatusEnum.DRAFT,
        // Fuente de verdad: `Subscription.collectionMethod` en API.
        collectionMethod: (sub.collectionMethod ?? null) as CollectionMethod | null,
        subtotal: sub.subtotal ?? 0,
        tax: sub.tax ?? 0,
        discount: sub.discount ?? 0,
        total: sub.total ?? 0,
        currencyId: null,
        notes: sub.notes ?? ''
      },
      { emitEvent: false }
    );
    if (sub.plan) {
      this.selectedPlan.set(sub.plan);
    } else {
      this.selectedPlan.set(null);
    }
    if (sub.currency && typeof sub.currency === 'string') {
      const cur = this.currencyOptions().find((c) => c.code === sub.currency);
      if (cur) this.generalForm.patchValue({ currencyId: cur.id });
    }
    this.invoicingForm.patchValue({
      paymentFormId: sub.billingTerms?.paymentForm?.id ?? null,
      paymentMethodId: sub.billingTerms?.paymentMethod?.id ?? null,
      cfdiUseId: sub.billingTerms?.cfdiUse?.id ?? null,
      autoInvoice: sub.autoInvoice ?? false
    });
    this.paymentTermsForm.patchValue(
      {
        frequency: sub.frequency ?? 'MONTHLY',
        collectionStartDate: parseApiDateTime(sub.collectionStartDate ?? undefined),
        billingDayOfMonth: sub.billingDayOfMonth ?? null,
        daysUntilExpiry: sub.daysUntilExpiry ?? null
      },
      { emitEvent: false }
    );
    this.syncSubscriptionPaymentTermsFromSaleType();
    this.syncPlanLockedCommercialFields();
    this.syncInvoicingControlsEnabledState();
    this.syncCustomerLockState();
    this.syncSubscriptionImmutableFieldsLockState();
    this.loadInvoiceCustomer(sub.customer?.id);
    if (this.dynamicFields().length) {
      rebuildAdditionalFieldsForm(
        this.fb,
        this.additionalFieldsForm,
        this.dynamicFields(),
        sub.additionalFields ?? null
      );
      this.syncAdditionalFieldsEnabledState();
      this.additionalFieldsRefresh.update((n) => n + 1);
    }
    this.cdr.markForCheck();
  }

  private lineToVm(l: SaleLineDTO): SaleLineVm {
    const taxes: CreateSaleLineTaxRequest[] = (l.taxes ?? []).map((t) => ({
      taxCode: t.tax?.taxKey,
      taxRate: t.taxRate,
      taxAmount: t.taxAmount,
      taxBase: t.taxBase,
      applicationType: toLineTaxApplicationTypeForCreate(t.applicationType as TaxApplicationType),
      collectionType: t.collectionType as 'SELLER_COLLECTS' | 'SELLER_WITHHOLDS' | undefined
    }));
    const product = l.product as ProductDTO | undefined;
    const satObjetoImp = l.satObjetoImp ?? (product?.satObjetoImpId ? { id: product.satObjetoImpId, name: product.satObjetoImpName ?? product.satObjetoImpId } : undefined);
    return {
      id: l.id,
      hash: l.hash,
      productId: l.product?.id,
      product: l.product,
      description: l.description ?? '',
      quantity: Number(l.quantity ?? 0),
      unitPrice: Number(l.unitPrice ?? 0),
      subtotal: Number(l.subtotal ?? 0),
      taxAmount: Number(l.taxAmount ?? 0),
      totalAmount: Number(l.totalAmount ?? 0),
      measureUnitId: l.measureUnit?.id,
      measureUnit: l.measureUnit,
      taxProductCodeId: l.taxProductCode?.id,
      taxProductCode: l.taxProductCode,
      satObjetoImpId: satObjetoImp?.id,
      satObjetoImp: satObjetoImp as SATObjetoImpDTO | undefined,
      taxExempt: l.taxExempt,
      taxInclusive: l.taxInclusive,
      taxes
    };
  }

  patchSale(inv: SaleDTO): void {
    this.generalForm.patchValue(
      {
        ...(this.isCustomerLocked()
          ? {}
          : {
              customerId: inv.customer?.id ?? null,
              customerSelection: inv.customer ? this.customerWithDisplayLabel(inv.customer) : null
            }),
        number: inv.number ?? '',
        processStatus: inv.processStatus ?? SalePipelineStatusEnum.OC,
        status: inv.status ?? SaleProcessStatusEnum.DRAFT,
        // Fuente de verdad: `Sale.collectionMethod` en API; `billingTerms` puede no traerlo o diferir.
        collectionMethod: (inv.collectionMethod ?? inv.billingTerms?.collectionMethod ?? CollectionMethodEnum.LINK) as CollectionMethod,
        subtotal: inv.subtotal ?? 0,
        tax: inv.tax ?? 0,
        discount: inv.discount ?? 0,
        total: inv.total ?? 0,
        currencyId: null,
        notes: inv.notes ?? ''
      },
      { emitEvent: false }
    );
    if (inv.currency && typeof inv.currency === 'string') {
      const cur = this.currencyOptions().find((c) => c.code === inv.currency);
      if (cur) this.generalForm.patchValue({ currencyId: cur.id });
    }
    if (inv.billingTerms) {
      const freq = inv.frequency ?? 'MONTHLY';
      this.invoicingForm.patchValue({
        paymentFormId: inv.billingTerms.paymentForm?.id ?? null,
        paymentMethodId: inv.billingTerms.paymentMethod?.id ?? null,
        cfdiUseId: inv.billingTerms.cfdiUse?.id ?? null,
        autoInvoice: inv.autoInvoice ?? false
      });
      this.paymentTermsForm.patchValue({
        daysUntilExpiry: inv.daysUntilExpiry ?? null,
        penaltyFeeAmount: inv.penaltyFeeAmount ?? null,
        penaltyInterestRate: inv.penaltyInterestRate ?? null,
        frequency: freq
      });
    } else {
      this.invoicingForm.patchValue({
        paymentFormId: null,
        paymentMethodId: null,
        cfdiUseId: null,
        autoInvoice: inv.autoInvoice ?? false
      });
    }
    this.syncInvoicingControlsEnabledState();
    this.syncSubscriptionPaymentTermsFromSaleType();
    this.syncCustomerLockState();
    this.invoicingForm.markAsPristine();
    this.generalTabDraftRefresh.update((n) => n + 1);
    this.loadInvoiceCustomer(this.generalForm.get('customerId')?.value as number | null);
    this.cdr.markForCheck();
  }

  onCustomerSelected(c: CustomerDTO | null): void {
    if (!c || this.isCustomerLocked()) return;
    this.generalForm.patchValue({
      customerId: c.id,
      customerSelection: this.customerWithDisplayLabel(c)
    });
    const id = c.id;
    if (id == null) return;
    if (this.isReadonly()) {
      this.loadInvoiceCustomer(id);
      return;
    }
    this.invoiceCustomerStatus.set('loading');
    this.customerService
      .getCustomerById(id)
      .pipe(catchError(() => of({ ok: false, body: null as CustomerDTO | null })))
      .subscribe((r) => {
        if (this.generalForm.get('customerId')?.value !== id) {
          return;
        }
        const customer = r.ok && r.body ? r.body : c;
        this.invoiceCustomer.set(customer);
        this.invoiceCustomerStatus.set('ready');
        this.applyPaymentTermsDefaultsForCustomer(customer);
        this.cdr.markForCheck();
      });
  }

  openCustomerDetails(): void {
    const customerId = this.generalForm.get('customerId')?.value as number | null;
    if (!customerId) return;
    this.openCustomerDetailsById(customerId);
  }

  openCustomerDetailsById(customerId: number): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: (err) => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
        console.error('Error loading customer by id:', err);
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  openSaleDetails(saleId: number): void {
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => {
              this.saleCharges.set(chargesResp.body ?? []);
            },
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  editSaleFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  onSaleReadOpenSubscription(_subscriptionId: number): void {
    this.closeSaleDetails();
  }

  private syncCustomerLockState(): void {
    const selection = this.generalForm.get('customerSelection');
    if (this.isCustomerLocked()) {
      selection?.disable({ emitEvent: false });
    } else {
      selection?.enable({ emitEvent: false });
    }
  }

  /** Deshabilita plan y fecha inicio de cobro en suscripciones existentes; método de cobro según reglas. */
  private syncSubscriptionImmutableFieldsLockState(): void {
    const planAndDateLocked = this.isSubscriptionImmutableFieldsLocked();

    const planCtrl = this.generalForm.get('planSelection');
    if (planCtrl) {
      if (planAndDateLocked) {
        planCtrl.disable({ emitEvent: false });
      } else {
        planCtrl.enable({ emitEvent: false });
      }
    }

    const collectionMethodCtrl = this.generalForm.get('collectionMethod');
    if (collectionMethodCtrl) {
      if (this.isCollectionMethodLocked()) {
        collectionMethodCtrl.disable({ emitEvent: false });
      } else {
        collectionMethodCtrl.enable({ emitEvent: false });
      }
    }

    const collectionStartCtrl = this.paymentTermsForm.get('collectionStartDate');
    if (collectionStartCtrl) {
      if (planAndDateLocked) {
        collectionStartCtrl.disable({ emitEvent: false });
      } else if (!this.isReadonly()) {
        collectionStartCtrl.enable({ emitEvent: false });
      }
    }

    this.cdr.markForCheck();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id || this.isCustomerLocked()) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSaleFromCustomer(saleId: number): void {
    if (!saleId) return;
    this.closeCustomerDetails();
    this.router.navigate(['/sales', saleId]);
  }

  /** Texto para el input del autocomplete: "nombre comercial - razón social". */
  displayCustomer(c?: CustomerDTO | null): string {
    if (!c) return '';
    const com = (c.comercialName ?? '').trim();
    const leg = (c.legalName ?? '').trim();
    if (com && leg) return `${com} - ${leg}`;
    return com || leg;
  }

  /** Añade `displayLabel` para `optionLabel` del p-autocomplete (objeto único). */
  private customerWithDisplayLabel(c: CustomerDTO): CustomerDTO & { displayLabel: string } {
    return { ...c, displayLabel: this.displayCustomer(c) };
  }

  displayProduct(p?: ProductDTO | null): string {
    if (!p) return '';
    return `${p.name} (${p.sku || p.id})`;
  }

  displayMeasureUnit(m?: MeasureUnitDTO | null): string {
    if (!m) return '';
    return `${m.code} - ${m.name}`;
  }

  displayTaxProductCode(t?: TaxProductCodeDTO | null): string {
    if (!t) return '';
    return `${t.taxCode} - ${t.taxDescription}`;
  }

  lineFormTaxes = signal<CreateSaleLineTaxRequest[]>([]);

  /** Origen de la línea al crear: catálogo (SKU) vs texto abierto. En edición queda fijo. */
  lineProductSource = signal<'CATALOG' | 'OPEN'>('CATALOG');

  lineProductSourceOptions: { label: string; value: 'CATALOG' | 'OPEN' }[] = [
    { label: 'Producto de catálogo', value: 'CATALOG' },
    { label: 'Producto abierto', value: 'OPEN' }
  ];

  /** Tooltip HTML (pTooltip con escape desactivado). */
  readonly lineProductSourceTooltip =
    '<strong>Producto de catálogo:</strong> eliges un artículo del inventario; se precargan descripción, precio, unidad, impuestos y datos fiscales del producto.<br><br>' +
    '<strong>Producto abierto:</strong> escribes la descripción y montos a mano, sin vincular un SKU. Útil para servicios o conceptos que no están dados de alta como producto.';

  /** PrimeNG emite el cambio; `ngModel` dentro de `formGroup` requiere `standalone` y además este evento es más fiable que `ngModelChange`. */
  onLineProductSourceSelectChange(ev: SelectButtonChangeEvent): void {
    const v = ev.value as 'CATALOG' | 'OPEN' | null | undefined;
    if (v !== 'CATALOG' && v !== 'OPEN') return;
    this.onLineProductSourceChange(v);
  }

  onLineProductSourceChange(mode: 'CATALOG' | 'OPEN'): void {
    if (this.lineModalMode() === 'edit') return;
    const prev = this.lineProductSource();
    this.lineProductSource.set(mode);
    if (mode === 'OPEN' && prev === 'CATALOG') {
      this.lineForm.patchValue({
        productSelection: null,
        productId: null
      });
      this.lineFormTaxes.set([]);
    }
    this.syncLineProductSelectionDisabled();
    this.cdr.markForCheck();
    setTimeout(() => this.focusLineEntryFirstField(), 100);
  }

  /** En línea abierta el producto del catálogo no aplica: control deshabilitado. */
  private syncLineProductSelectionDisabled(): void {
    const ctrl = this.lineForm.get('productSelection');
    if (!ctrl) return;
    if (this.lineProductSource() === 'OPEN') {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  onProductSelected(p: ProductDTO | null): void {
    if (!p) return;
    const satObjetoImp = p.satObjetoImpId
      ? this.satObjetoImpOptions().find((s) => s.id === p.satObjetoImpId) ?? { id: p.satObjetoImpId, name: p.satObjetoImpName ?? p.satObjetoImpId }
      : null;
    this.lineForm.patchValue({
      productId: p.id,
      productSelection: p,
      description: p.name ?? this.lineForm.get('description')?.value,
      unitPrice: p.unitPrice ?? 0,
      measureUnitSelection: p.measureUnitId
        ? { id: p.measureUnitId, code: p.measureUnitCode ?? '', name: p.measureUnitName ?? '' }
        : null,
      measureUnitId: p.measureUnitId,
      taxProductCodeSelection: p.taxCodeId
        ? {
            id: p.taxCodeId,
            taxCode: p.taxCodeTaxCode ?? '',
            taxDescription: p.taxCodeDescription ?? ''
          }
        : null,
      taxProductCodeId: p.taxCodeId,
      satObjetoImpSelection: satObjetoImp,
      satObjetoImpId: p.satObjetoImpId ?? null
    });
    const taxes: CreateSaleLineTaxRequest[] = (p.taxes ?? []).map((t) => ({
      taxCode: t.tax?.taxKey,
      taxRate: t.taxRate ?? t.tax?.rate,
      taxAmount: t.taxAmount,
      taxBase: 0,
      applicationType: toLineTaxApplicationTypeForCreate((t.applicationType ?? 'TAXABLE') as TaxApplicationType),
      collectionType: t.collectionType ?? 'SELLER_COLLECTS'
    }));
    this.lineFormTaxes.set(taxes);
    this.editingLineTaxIndex.set(null);
  }

  /**
   * Menú del split «Cobro»: acción principal = abrir recibo; desplegable = copiar enlace público del recibo.
   */
  cobroSplitMenuItems = computed((): { label: string; icon: string; command: () => void }[] => [
    {
      label: 'Copiar enlace',
      icon: 'pi pi-copy',
      command: () => this.copyFirstReceiptPublicLink()
    }
  ]);

  /** Menú del split CFDI timbrado: descargas secundarias (el clic principal abre el visor PDF). */
  saleStampSplitMenuItems = computed((): { label: string; icon: string; disabled: boolean; command: () => void }[] => {
    const ld = this.loading();
    const pdf = this.downloadPdfLoading();
    const xml = this.downloadXmlLoading();
    return [
      {
        label: 'Descargar PDF',
        icon: 'pi pi-download',
        disabled: ld || pdf,
        command: () => this.downloadStampPDF()
      },
      {
        label: 'Descargar XML',
        icon: 'pi pi-code',
        disabled: ld || xml,
        command: () => this.downloadStampXML()
      }
    ];
  });

  /**
   * Comparativa UI: mismas acciones que la barra superior, expuestas con `p-menubar` (no sustituye a la barra).
   */
  saleCompareMenubarItems = computed((): MenuItem[] => {
    const ld = this.loading();
    const pdf = this.downloadPdfLoading();
    const pdfView = this.cfdiPdfViewerOpening();
    const xml = this.downloadXmlLoading();
    const ch = this.chargesLoading();
    const copyBusy = this.receiptLinkCopyLoading();
    const stamped = this.currentSale()?.isStamped === true;
    const invId = this.linkedInvoiceId();
    const canEmit = this.canEmitCfdi();
    return [
      {
        label: 'Fiscal',
        icon: 'pi pi-file-check',
        items: [
          {
            label: 'Factura',
            icon: 'pi pi-file',
            disabled: ld,
            tooltip: invId ? `Abrir factura #${invId}` : 'Sin factura vinculada; aviso al pulsar',
            tooltipPosition: 'left',
            command: () => this.openLinkedInvoice()
          },
          {
            label: 'CFDI',
            icon: 'pi pi-file-export',
            disabled: ld || !canEmit,
            command: () => this.emitCfdi()
          },
          {
            label: 'Ver PDF',
            icon: 'pi pi-eye',
            disabled: ld || pdfView || pdf || !stamped,
            command: () => this.openCfdiPdfViewer()
          },
          {
            label: 'XML',
            icon: 'pi pi-code',
            disabled: ld || xml || !stamped,
            command: () => this.downloadStampXML()
          }
        ]
      },
      {
        label: 'Cobro',
        icon: 'pi pi-credit-card',
        items: [
          {
            label: 'Ver recibo',
            icon: 'pi pi-receipt',
            disabled: ld || ch,
            tooltip: 'Primer cargo con link (split y botón Recibo de la barra)',
            tooltipPosition: 'left',
            command: () => this.openFirstReceiptShortcut()
          },
          {
            label: 'Copiar enlace',
            icon: 'pi pi-copy',
            disabled: ld || ch || copyBusy,
            tooltip: 'Copiar al portapapeles el enlace público del recibo (primer cargo con link)',
            tooltipPosition: 'left',
            command: () => this.copyFirstReceiptPublicLink()
          }
        ]
      }
    ];
  });

  /**
   * Envía el formulario (alta o actualización). Siempre cierra operativa (`ISSUED`); no hay borrador.
   */
  onSubmitWithStatus(status: 'DRAFT' | 'ISSUED' = 'ISSUED'): void {
    // Validar campos básicos usando la lógica de canSaveDraft
    if (!this.canSaveDraft()) {
      this.generalForm.markAllAsTouched();
      this.invoicingForm.markAllAsTouched();
      this.paymentTermsForm.markAllAsTouched();
      this.additionalFieldsForm.markAllAsTouched();
      this.toastr.error(
        'Completa los datos obligatorios: cliente, moneda, términos de pago y facturación.'
      );
      return;
    }

    // Validaciones estrictas al crear/actualizar
    if (this.paymentTermsForm.invalid) {
      this.paymentTermsForm.markAllAsTouched();
      this.toastr.error('Verifica los términos de pago y fechas.');
      return;
    }
    if (this.invoicingForm.invalid) {
      this.invoicingForm.markAllAsTouched();
      this.toastr.error('Verifica los datos de facturación.');
      return;
    }
    if (this.generalForm.invalid) {
      this.generalForm.markAllAsTouched();
      this.toastr.error('Verifica los datos generales de la venta.');
      return;
    }
    if (this.additionalFieldsForm.invalid) {
      this.additionalFieldsForm.markAllAsTouched();
      this.toastr.error('Verifica los campos adicionales.');
      return;
    }
    if (this.lines().length === 0) {
      this.toastr.error('Agrega al menos una línea para continuar.');
      return;
    }

    const customerId = this.generalForm.get('customerId')?.value;
    const currencyId = this.generalForm.get('currencyId')?.value;
    if (!customerId || !currencyId) {
      this.toastr.error('Cliente y moneda son obligatorios.');
      return;
    }

    const autoInvoiceWarning = this.autoInvoiceCustomerWarning();
    if (autoInvoiceWarning) {
      this.toastr.error(autoInvoiceWarning);
      return;
    }

    const isEdit = this.isEditMode() && this.editingSubscriptionId() != null;
    if (!isEdit && this.catchUpRequired()) {
      this.pendingSubmitStatus = status;
      this.catchUpConfirmVisible.set(true);
      return;
    }
    if (isEdit && this.billingCycleChangeRequired()) {
      this.pendingSubmitStatus = status;
      this.billingCycleChangeSelection.set(null);
      if (!this.salesLoaded) this.loadSubscriptionSales();
      this.billingCycleChangeVisible.set(true);
      return;
    }

    this.pendingBillingCycleChangeMode = undefined;
    this.executeSubmit(status);
  }

  /**
   * Misma regla que el backend: hay cambio de ciclo si cambia la frecuencia, o el día de cobro
   * en mensual/trimestral/anual, y la suscripción tiene ciclo activo.
   */
  billingCycleChangeRequired(): boolean {
    const sub = this.currentSubscription();
    if (!sub?.nextBillingDate) return false;
    const status = sub.status;
    if (
      status !== SubscriptionStatusEnum.ACTIVE &&
      status !== SubscriptionStatusEnum.TRAILING &&
      status !== SubscriptionStatusEnum.PAST_DUE
    ) {
      return false;
    }
    const { frequency, billingDay } = this.requestedBillingCycle();
    const dayChanged =
      this.billingDayOfMonthAppliesToFrequency(frequency) && billingDay !== (sub.billingDayOfMonth ?? null);
    return frequency !== sub.frequency || dayChanged;
  }

  /** Frecuencia y día efectivos del update (el backend conserva el día guardado si no se envía). */
  private requestedBillingCycle(): { frequency: FrequencyEnumValue; billingDay: number | null } {
    const sub = this.currentSubscription();
    const pt = this.paymentTermsForm.getRawValue();
    const frequency = (this.generalForm.getRawValue().planId != null
      ? sub?.frequency ?? pt.frequency
      : pt.frequency ?? 'MONTHLY') as FrequencyEnumValue;
    const requestedDay =
      this.billingDayOfMonthAppliesToFrequency(frequency) && pt.billingDayOfMonth != null
        ? Number(pt.billingDayOfMonth)
        : null;
    return { frequency, billingDay: requestedDay ?? sub?.billingDayOfMonth ?? null };
  }

  /** Fechas de cada opción y motivo por el que no se puede corregir el periodo actual (si aplica). */
  billingCycleChangePreview(): {
    frequencyChanged: boolean;
    previousFrequencyLabel: string;
    newFrequencyLabel: string;
    billingDayChanged: boolean;
    previousBillingDay: number | null;
    newBillingDay: number | null;
    currentNextBillingDate: Date | null;
    nextCycleFollowingDate: Date | null;
    transitionLonger: boolean | null;
    currentChargeDate: Date | null;
    currentChargeTotal: number | null;
    newTotal: number;
    currentChargePaid: boolean;
    pastChargesCount: number;
    adjustedNextBillingDate: Date | null;
    adjustBlockedReason: string | null;
  } {
    const sub = this.currentSubscription();
    const { frequency, billingDay } = this.requestedBillingCycle();
    const previousBillingDay = sub?.billingDayOfMonth ?? null;
    const currentNextBillingDate = parseLocalDate(sub?.nextBillingDate);
    const nextCycleFollowingDate = currentNextBillingDate
      ? computeNextBillingDate(currentNextBillingDate, frequency, billingDay)
      : null;
    const billingDayChanged =
      this.billingDayOfMonthAppliesToFrequency(frequency) && billingDay !== previousBillingDay;
    const followingWithPreviousDay = currentNextBillingDate
      ? computeNextBillingDate(currentNextBillingDate, frequency, previousBillingDay)
      : null;
    const transitionLonger =
      billingDayChanged && nextCycleFollowingDate && followingWithPreviousDay
        ? nextCycleFollowingDate.getTime() > followingWithPreviousDay.getTime()
        : null;

    const preview = {
      frequencyChanged: frequency !== sub?.frequency,
      previousFrequencyLabel: this.getFrequencyLabel(sub?.frequency),
      newFrequencyLabel: this.getFrequencyLabel(frequency),
      billingDayChanged,
      previousBillingDay,
      newBillingDay: billingDay,
      currentNextBillingDate,
      nextCycleFollowingDate,
      transitionLonger,
      currentChargeDate: null as Date | null,
      currentChargeTotal: null as number | null,
      newTotal: Math.max(0, this.totals().total),
      currentChargePaid: false,
      pastChargesCount: 0,
      adjustedNextBillingDate: null as Date | null,
      adjustBlockedReason: null as string | null
    };
    const blocked = (reason: string) => ({ ...preview, adjustBlockedReason: `No disponible: ${reason}` });
    if (!preview.frequencyChanged) {
      return blocked('solo aplica cuando cambia la frecuencia de cobro. Usa la otra opción.');
    }
    if (sub?.collectionMethod === CollectionMethodEnum.RECURRING_CHARGE) {
      return blocked('no se puede usar con cargo recurrente. Usa la otra opción.');
    }
    if (this.salesLoading()) {
      return { ...preview, adjustBlockedReason: 'Revisando los cobros de la suscripción…' };
    }
    const activeSales = this.subscriptionSales()
      .filter((s) => s.status !== InvoiceStatusEnum.CANCELLED && s.state !== EntityState.INACTIVE && !!s.collectionStartDate)
      .map((s) => ({ sale: s, start: parseLocalDate(s.collectionStartDate)! }))
      .filter((s) => !!s.start)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    if (activeSales.length === 0) {
      return blocked('la suscripción todavía no tiene un cobro del periodo actual.');
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const started = activeSales.filter((s) => s.start.getTime() <= today.getTime());
    const current = started.length ? started[started.length - 1] : activeSales[0];
    preview.currentChargeDate = current.start;
    preview.currentChargeTotal = current.sale.total ?? null;
    preview.pastChargesCount = activeSales.filter((s) => s.start.getTime() < current.start.getTime()).length;
    if (activeSales.some((s) => s !== current && s.start.getTime() >= current.start.getTime())) {
      return blocked('hay cobros adelantados posteriores al periodo actual. Usa la otra opción.');
    }
    if (!currentNextBillingDate || !sub?.frequency) {
      return blocked('no se pudo revisar el cobro actual.');
    }
    const expectedCurrentEnd = computeNextBillingDate(current.start, sub.frequency, sub.billingDayOfMonth ?? null);
    if (expectedCurrentEnd.getTime() !== currentNextBillingDate.getTime()) {
      return blocked('la fecha del próximo cobro ya no coincide con el cobro actual. Usa la otra opción.');
    }
    if (current.sale.isStamped) {
      return blocked('el cobro del periodo actual ya está facturado. Usa la otra opción.');
    }
    const saleTotal = current.sale.total ?? 0;
    preview.currentChargePaid =
      current.sale.status === InvoiceStatusEnum.PAID ||
      current.sale.status === InvoiceStatusEnum.PARTIALLY_PAID ||
      (current.sale.amountDue != null && current.sale.amountDue < saleTotal);
    if (preview.currentChargePaid && Math.abs(preview.newTotal - saleTotal) >= 0.005) {
      return blocked('el cobro del periodo actual ya está pagado y el monto nuevo es distinto. Usa la otra opción.');
    }
    const adjustedNextBillingDate = computeNextBillingDate(current.start, frequency, billingDay);
    preview.adjustedNextBillingDate = adjustedNextBillingDate;
    if (adjustedNextBillingDate.getTime() <= today.getTime()) {
      return blocked('con la nueva frecuencia, el siguiente cobro ya habría pasado. Usa la otra opción.');
    }
    return preview;
  }

  selectBillingCycleChangeMode(mode: BillingCycleChangeModeValue): void {
    if (mode === 'ADJUST_CURRENT_PERIOD' && this.billingCycleChangePreview().adjustBlockedReason) return;
    this.billingCycleChangeSelection.set(mode);
  }

  cancelBillingCycleChange(): void {
    if (this.loading()) return;
    this.billingCycleChangeVisible.set(false);
    this.pendingBillingCycleChangeMode = undefined;
  }

  confirmBillingCycleChangeAndSubmit(): void {
    const mode = this.billingCycleChangeSelection();
    if (this.loading() || !mode) return;
    this.pendingBillingCycleChangeMode = mode;
    this.billingCycleChangeVisible.set(false);
    this.executeSubmit(this.pendingSubmitStatus);
  }

  cancelCatchUpConfirm(): void {
    if (this.loading()) return;
    this.catchUpConfirmVisible.set(false);
  }

  onPaymentTermsCatchUpInput(): void {
    this.refreshCatchUpPreview();
  }

  private refreshCatchUpPreview(): void {
    if (this.isEditMode()) {
      this.catchUpPreview.set({
        sales: [],
        nextBillingDate: null,
        grouped: false,
        catchUpCycleCount: 0,
        cycleLabels: [],
        regularCycleLabel: null
      });
      return;
    }
    const pt = this.paymentTermsForm.getRawValue();
    const frequency = (pt.frequency ?? 'MONTHLY') as FrequencyEnumValue;
    const collectionStartDate = formValueToLocalDate(pt.collectionStartDate);
    const t = this.totals();
    const discount = Number(this.generalForm.getRawValue().discount ?? 0);
    const amounts = {
      subtotal: t.subtotal,
      tax: Math.max(0, t.tax),
      discount,
      total: Math.max(0, t.total)
    };

    const billingDayOfMonth = this.billingDayOfMonthAppliesToFrequency(frequency)
      ? pt.billingDayOfMonth
      : null;

    const catchUpCycleCount = resolveCatchUpBillingState({
      collectionStartDate,
      billingDayOfMonth,
      frequency
    }).catchUpCycles.length;
    if (catchUpCycleCount <= 1 && this.groupCatchUpCycles()) {
      this.groupCatchUpCycles.set(false);
    }

    this.catchUpPreview.set(
      previewCatchUpBillingCycles({
        collectionStartDate,
        billingDayOfMonth,
        frequency,
        groupCatchUp: catchUpCycleCount > 1 && this.groupCatchUpCycles(),
        amounts
      })
    );
    this.cdr.markForCheck();
  }

  onGroupCatchUpCyclesChange(value: boolean): void {
    this.groupCatchUpCycles.set(!!value);
    this.refreshCatchUpPreview();
  }

  confirmCatchUpAndSubmit(): void {
    if (this.loading()) return;
    this.catchUpConfirmVisible.set(false);
    this.executeSubmit(this.pendingSubmitStatus);
  }

  catchUpPreviewTotal(): number {
    return this.catchUpPreviewSales().reduce((sum, sale) => sum + (sale.total ?? 0), 0);
  }

  catchUpBillingFrequency(): FrequencyEnumValue {
    return (this.paymentTermsForm.getRawValue().frequency ?? 'MONTHLY') as FrequencyEnumValue;
  }

  catchUpCyclesUnitForCount(count: number): string {
    return catchUpCyclesUnitLabel(this.catchUpBillingFrequency(), count);
  }

  catchUpPastCyclesDescription(): string {
    const frequency = this.catchUpBillingFrequency();
    switch (frequency) {
      case 'DAILY':
        return 'días vencidos';
      case 'WEEKLY':
        return 'semanas vencidas';
      case 'BIWEEKLY':
        return 'quincenas vencidas';
      case 'MONTHLY':
        return 'meses vencidos';
      case 'QUARTERLY':
        return 'trimestres vencidos';
      case 'SEMIANNUALLY':
        return 'semestres vencidos';
      case 'ANNUALLY':
        return 'años vencidos';
      default:
        return 'ciclos vencidos';
    }
  }

  catchUpCurrentCycleDescription(): string {
    const frequency = this.catchUpBillingFrequency();
    switch (frequency) {
      case 'DAILY':
        return 'día en curso';
      case 'WEEKLY':
        return 'semana en curso';
      case 'BIWEEKLY':
        return 'quincena en curso';
      case 'MONTHLY':
        return 'mes en curso';
      case 'QUARTERLY':
        return 'trimestre en curso';
      case 'SEMIANNUALLY':
        return 'semestre en curso';
      case 'ANNUALLY':
        return 'año en curso';
      default:
        return 'ciclo en curso';
    }
  }

  private executeSubmit(status: 'DRAFT' | 'ISSUED' = 'ISSUED'): void {
    this.loading.set(true);

    const isEdit = this.isEditMode() && this.editingSubscriptionId() != null;
    const billingCycleChangeMode = isEdit ? this.pendingBillingCycleChangeMode : undefined;
    let req$: ReturnType<SubscriptionService['createSubscription']>;
    try {
      if (isEdit) {
        req$ = this.subscriptionService.updateSubscription(
          this.editingSubscriptionId()!,
          this.buildUpdateSubscriptionRequest()
        );
      } else {
        req$ = this.subscriptionService.createSubscription(this.buildSubscriptionRequest('ISSUED'));
      }
    } catch (e) {
      this.loading.set(false);
      this.toastr.error(e instanceof Error ? e.message : 'Error al preparar la suscripción');
      return;
    }

    req$
      .pipe(finalize(() => {
        this.loading.set(false);
        this.pendingBillingCycleChangeMode = undefined;
      }))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            if (billingCycleChangeMode && billingCycleChangeMode !== 'NEXT_CYCLE' && this.salesLoaded) {
              this.loadSubscriptionSales();
            }
            const id = r.body?.id;
            if (id) {
              this.isEditMode.set(true);
              this.editingSubscriptionId.set(id);
              this.currentSubscription.set(r.body!);
              this.resetSubscriptionBillingHashBaseline(r.body!);
              this.syncCustomerLockState();
              if (r.body?.number != null) {
                this.generalForm.patchValue({ number: r.body.number }, { emitEvent: false });
              }
              this.syncInvoicingControlsEnabledState();
              this.router.navigate(['/subscriptions', id], { replaceUrl: true });
            }
            this.toastr.success(
              isEdit
                ? 'Suscripción actualizada correctamente'
                : 'Suscripción creada correctamente'
            );
          } else {
            this.setError(r.message || 'Error al guardar la suscripción');
          }
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al conectar con el servidor');
        }
      });
  }

  private buildSubscriptionRequest(status: 'DRAFT' | 'ISSUED' = 'DRAFT'): CreateSubscriptionRequest {
    const base = this.buildSubscriptionPayloadBase();
    const operationalStatusPayload =
      status === 'ISSUED' ? InvoiceStatusEnum.ISSUED : InvoiceStatusEnum.DRAFT;

    return {
      ...base,
      customerId: Number(this.generalForm.getRawValue().customerId),
      status: operationalStatusPayload,
      penaltyFeeAmount: this.paymentTermsForm.getRawValue().penaltyFeeAmount,
      penaltyInterestRate: this.paymentTermsForm.getRawValue().penaltyInterestRate,
      groupCatchUpCycles: this.catchUpRequired() ? this.groupCatchUpCycles() : undefined
    };
  }

  private buildUpdateSubscriptionRequest(): UpdateSubscriptionRequest {
    this.validateImmutableSubscriptionFieldsOnUpdate();
    return {
      ...this.buildSubscriptionPayloadBase(),
      billingCycleChangeMode: this.pendingBillingCycleChangeMode
    };
  }

  /**
   * Valida en cliente campos inmutables al editar: plan, fecha de inicio de cobro
   * y cambios de método de cobro según reglas de Cargo Recurrente.
   */
  private validateImmutableSubscriptionFieldsOnUpdate(): void {
    const sub = this.currentSubscription();
    if (!sub || this.editingSubscriptionId() == null) return;

    const iv = this.generalForm.getRawValue();
    const pt = this.paymentTermsForm.getRawValue();

    const currentPlanId = sub.plan?.id ?? null;
    const requestedPlanId = iv.planId != null ? Number(iv.planId) : null;
    if (currentPlanId !== requestedPlanId) {
      throw new Error('El plan de la suscripción no se puede modificar.');
    }

    this.validateCollectionMethodChangeOnUpdate(
      (sub.collectionMethod ?? null) as CollectionMethod | null,
      (iv.collectionMethod ?? null) as CollectionMethod | null
    );

    const currentStart = parseApiDateTime(sub.collectionStartDate ?? undefined);
    const requestedStart = formValueToLocalDate(pt.collectionStartDate);
    if (!this.sameCalendarDate(currentStart, requestedStart)) {
      throw new Error('La fecha de inicio de cobro de la suscripción no se puede modificar.');
    }
  }

  private validateCollectionMethodChangeOnUpdate(
    current: CollectionMethod | null,
    requested: CollectionMethod | null
  ): void {
    if (current === requested) return;
    if (current === CollectionMethodEnum.RECURRING_CHARGE) {
      throw new Error('El método de cobro Cargo Recurrente no se puede modificar.');
    }
    if (requested === CollectionMethodEnum.RECURRING_CHARGE) {
      throw new Error('No se puede cambiar el método de cobro a Cargo Recurrente.');
    }
  }

  private sameCalendarDate(a: Date | null, b: Date | null): boolean {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private buildSubscriptionPayloadBase(): UpdateSubscriptionRequest {
    const iv = this.generalForm.getRawValue();
    const inv = this.invoicingForm.getRawValue();
    const pt = this.paymentTermsForm.getRawValue();
    const billingTerms: CreateSaleBillingTerms = {
      billingType: 'PROFORMA',
      collectionMethod: iv.collectionMethod as CollectionMethod,
      cfdiUseId: inv.cfdiUseId != null ? String(inv.cfdiUseId) : undefined
    };

    const lines: CreateSaleLineRequest[] = this.lines().map((l) => {
      const taxAmount = l.taxes?.reduce((s, t) => s + (Number(t.taxAmount) || 0), 0) ?? 0;
      const totalAmount = Number(l.quantity) * Number(l.unitPrice) + taxAmount;
      const safeTaxAmount = Math.max(0, Number(l.taxAmount) ?? taxAmount);
      const safeTotalAmount = Math.max(0, Number(l.totalAmount) ?? totalAmount);
      return {
        ...(l.id != null ? { id: l.id } : {}),
        productId: l.productId,
        description: l.description,
        measureUnitId: l.measureUnitId,
        taxProductCodeId: l.taxProductCodeId,
        satObjetoImpId: l.satObjetoImpId,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
        subtotal: Number(l.subtotal),
        taxAmount: safeTaxAmount,
        totalAmount: safeTotalAmount,
        taxExempt: l.taxExempt,
        taxInclusive: l.taxInclusive,
        taxes: l.taxes?.length ? l.taxes : undefined,
        metadata: undefined
      };
    });

    const paymentFormId = inv.paymentFormId != null ? Number(inv.paymentFormId) : undefined;
    const paymentMethodId = inv.paymentMethodId != null ? Number(inv.paymentMethodId) : undefined;
    const t = this.totals();

    return {
      planId: iv.planId != null ? Number(iv.planId) : undefined,
      billingTerms,
      paymentFormId,
      paymentMethodId,
      collectionStartDate: formValueToApiDateTime(pt.collectionStartDate),
      billingDayOfMonth:
        this.billingDayOfMonthAppliesToFrequency(pt.frequency) &&
        pt.billingDayOfMonth != null
          ? Number(pt.billingDayOfMonth)
          : undefined,
      daysUntilExpiry: pt.daysUntilExpiry != null ? Number(pt.daysUntilExpiry) : undefined,
      autoInvoice: !!inv.autoInvoice,
      frequency: pt.frequency ?? 'MONTHLY',
      subtotal: t.subtotal,
      tax: Math.max(0, t.tax),
      discount: Number(iv.discount ?? 0),
      total: Math.max(0, t.total),
      currencyId: Number(iv.currencyId),
      notes: iv.notes ? String(iv.notes).trim() : undefined,
      lines: lines.length ? lines : undefined,
      metadata: undefined,
      additionalFields: serializeAdditionalFields(this.dynamicFields(), this.additionalFieldsForm),
      ...(this.editingSubscriptionId() != null
        ? { number: String(iv.number ?? '').trim() }
        : {})
    };
  }

  cancel(): void {
    this.router.navigate(['/subscriptions']);
  }

  emitCfdi(): void {
    this.toastr.info('El timbrado CFDI aplica solo a facturas, no a ventas.');
  }

  loadCharges(): void {
    const id = this.editingSaleId();
    if (id == null) return;
    this.chargesLoading.set(true);
    this.expandedChargeIds.set(new Set());
    this.chargeAttemptsById.set({});
    this.chargeAttemptsLoadingIds.set(new Set());
    this.saleService
      .getSaleCharges(id)
      .pipe(finalize(() => this.chargesLoading.set(false)))
      .subscribe({
        next: (r) => this.charges.set(r.ok && r.body ? r.body : []),
        error: () => this.charges.set([])
      });
  }

  isChargeExpanded(chargeId: number | undefined): boolean {
    if (chargeId == null) return false;
    return this.expandedChargeIds().has(chargeId);
  }

  isChargeAttemptsLoading(chargeId: number | undefined): boolean {
    if (chargeId == null) return false;
    return this.chargeAttemptsLoadingIds().has(chargeId);
  }

  getChargeAttempts(chargeId: number | undefined): PaymentAttemptDTO[] {
    if (chargeId == null) return [];
    return this.chargeAttemptsById()[chargeId] ?? [];
  }

  /**
   * Expande/colapsa la fila del recibo. Los intentos se consultan solo al expandir
   * (y se cachean hasta refrescar la lista de cargos).
   */
  toggleChargeExpanded(charge: ChargeDTO): void {
    const chargeId = charge.id;
    if (chargeId == null) return;

    const wasExpanded = this.expandedChargeIds().has(chargeId);
    this.expandedChargeIds.update((set) => {
      const next = new Set(set);
      if (next.has(chargeId)) next.delete(chargeId);
      else next.add(chargeId);
      return next;
    });

    if (wasExpanded) return;
    if (this.chargeAttemptsById()[chargeId] != null) return;
    this.loadChargeAttempts(chargeId);
  }

  private loadChargeAttempts(chargeId: number): void {
    this.chargeAttemptsLoadingIds.update((set) => {
      const next = new Set(set);
      next.add(chargeId);
      return next;
    });
    this.chargeService
      .listPaymentAttempts(chargeId)
      .pipe(
        finalize(() => {
          this.chargeAttemptsLoadingIds.update((set) => {
            const next = new Set(set);
            next.delete(chargeId);
            return next;
          });
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (r) => {
          this.chargeAttemptsById.update((map) => ({
            ...map,
            [chargeId]: r.ok && r.body ? r.body : []
          }));
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al cargar los intentos de pago'));
        }
      });
  }

  /**
   * Abre el menú overlay de acciones del recibo (Reenviar liga).
   */
  openChargeActionsMenu(event: Event, charge: ChargeDTO): void {
    const canRecall = this.canSendPaymentLink(charge);
    this.chargeRowMenuItems.set([
      {
        label: 'Enviar liga de pago',
        icon: 'pi pi-envelope',
        disabled: !canRecall || this.recallLoading(),
        command: () => this.openRecallEmailDialog(charge)
      }
    ]);
    this.chargeActionsMenu?.toggle(event);
  }

  private canSendPaymentLink(charge: ChargeDTO): boolean {
    if (charge.id == null) return false;
    const unpaid =
      charge.status === ChargeOperationalStatusEnum.PENDING ||
      charge.status === ChargeOperationalStatusEnum.DUE ||
      charge.status === ChargeOperationalStatusEnum.FAILED;
    if (!unpaid) return false;
    return (
      charge.collectionMethod === CollectionMethodEnum.LINK ||
      charge.collectionMethod === CollectionMethodEnum.MANUAL
    );
  }

  openRecallEmailDialog(charge: ChargeDTO): void {
    if (charge.id == null) return;
    const defaultEmail =
      charge.paymentLink?.customer?.email ??
      this.currentSale()?.customer?.email ??
      '';
    this.recallChargeId.set(charge.id);
    this.recallEmail.set(defaultEmail);
    this.recallConvertsManualCharge.set(charge.collectionMethod === CollectionMethodEnum.MANUAL);
    this.recallEmailDialogVisible.set(true);
  }

  closeRecallEmailDialog(): void {
    if (this.recallLoading()) return;
    this.recallEmailDialogVisible.set(false);
    this.recallChargeId.set(null);
    this.recallEmail.set('');
    this.recallConvertsManualCharge.set(false);
  }

  submitRecallEmail(): void {
    const chargeId = this.recallChargeId();
    const email = this.recallEmail().trim();
    const convertingManual = this.recallConvertsManualCharge();
    if (chargeId == null) return;
    if (!email) {
      this.toastr.error('Indica el correo electrónico al que se enviará la liga.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.recallLoading.set(true);
    this.chargeService
      .recall(chargeId, email)
      .pipe(finalize(() => this.recallLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            if (r.body) {
              const updated = r.body;
              this.charges.update((list) =>
                list.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
              );
            }
            this.toastr.success(
              r.message ??
                (convertingManual
                  ? 'Liga de pago creada y correo enviado'
                  : 'Correo de liga de pago enviado')
            );
            this.recallEmailDialogVisible.set(false);
            this.recallChargeId.set(null);
            this.recallEmail.set('');
            this.recallConvertsManualCharge.set(false);
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo enviar el correo'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al enviar el correo'));
        }
      });
  }

  onTabChange(value: string | number | undefined): void {
    if (value == null) return;
    const tab = String(value);
    if ((tab === 'sales' || tab === 'auditoria') && this.editingSubscriptionId() == null) return;
    this.activeTab.set(tab);
    if (tab === 'sales' && !this.salesLoaded) {
      this.loadSubscriptionSales();
    }
  }

  loadSubscriptionSales(): void {
    const id = this.editingSubscriptionId();
    if (id == null) return;

    this.salesLoading.set(true);
    this.saleService
      .searchSales({ subscriptionId: id }, { page: 0, size: 100, sort: ['createdAt,desc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as SaleDTO[] })),
        finalize(() => {
          this.salesLoading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe((r) => {
        this.subscriptionSales.set(r.ok && r.body ? r.body : []);
        this.salesLoaded = true;
      });
  }

  openAdvanceCyclesDialog(): void {
    if (this.editingSubscriptionId() == null) return;
    this.advanceCycles.set(1);
    this.advanceGroupCycles.set(false);
    this.advancePreview.set(null);
    this.advanceCyclesDialogVisible.set(true);
    this.scheduleAdvancePreview();
  }

  closeAdvanceCyclesDialog(): void {
    if (this.advanceSubmitting()) return;
    if (this.advancePreviewTimer != null) {
      clearTimeout(this.advancePreviewTimer);
      this.advancePreviewTimer = null;
    }
    this.advanceCyclesConfirmVisible.set(false);
    this.advanceCyclesDialogVisible.set(false);
    this.advancePreview.set(null);
  }

  onAdvanceCyclesChange(value: number | null): void {
    const cycles = value != null && value >= 1 ? Math.min(Math.floor(value), 36) : 1;
    this.advanceCycles.set(cycles);
    this.scheduleAdvancePreview();
  }

  onAdvanceGroupCyclesChange(value: boolean): void {
    this.advanceGroupCycles.set(!!value);
    this.scheduleAdvancePreview();
  }

  private scheduleAdvancePreview(): void {
    if (this.advancePreviewTimer != null) {
      clearTimeout(this.advancePreviewTimer);
    }
    this.advancePreviewTimer = setTimeout(() => this.loadAdvancePreview(), 400);
  }

  private loadAdvancePreview(): void {
    const id = this.editingSubscriptionId();
    if (id == null || !this.advanceCyclesDialogVisible()) return;

    const cycles = this.advanceCycles();
    if (cycles < 1) {
      this.advancePreview.set(null);
      return;
    }

    this.advancePreviewLoading.set(true);
    this.subscriptionService
      .previewAdvanceBillingCycles(id, {
        cycles,
        groupCycles: this.advanceGroupCycles()
      })
      .pipe(
        catchError((err) => {
          this.toastr.error(httpErrorApiMessage(err, `No se pudo simular el adelanto de ${this.advanceCyclesUnitLabelLower()}`));
          return of(null);
        }),
        finalize(() => {
          this.advancePreviewLoading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe((r) => {
        if (r?.ok && r.body) {
          this.advancePreview.set(r.body);
        } else if (r != null) {
          this.advancePreview.set(null);
          this.toastr.error(apiFailureMessage(r, `No se pudo simular el adelanto de ${this.advanceCyclesUnitLabelLower()}`));
        } else {
          this.advancePreview.set(null);
        }
      });
  }

  submitAdvanceCycles(): void {
    const id = this.editingSubscriptionId();
    if (id == null || this.advanceSubmitting()) return;

    const cycles = this.advanceCycles();
    if (cycles < 1) {
      this.toastr.warning(`Indica al menos un ${this.advanceCyclesUnitForCount(1)} a adelantar`);
      return;
    }
    if (this.advancePreviewSales().length === 0) {
      this.toastr.warning('Espera a que termine la simulación antes de adelantar');
      return;
    }
    this.advanceCyclesConfirmVisible.set(true);
  }

  cancelAdvanceCyclesConfirm(): void {
    if (this.advanceSubmitting()) return;
    this.advanceCyclesConfirmVisible.set(false);
  }

  executeAdvanceCycles(): void {
    const id = this.editingSubscriptionId();
    if (id == null || this.advanceSubmitting()) return;

    const cycles = this.advanceCycles();
    if (cycles < 1) return;

    this.advanceSubmitting.set(true);
    this.subscriptionService
      .advanceBillingCycles(id, {
        cycles,
        groupCycles: this.advanceGroupCycles()
      })
      .pipe(
        finalize(() => {
          this.advanceSubmitting.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (r) => {
          if (!r.ok) {
            this.toastr.error(apiFailureMessage(r, `No se pudieron adelantar los ${this.advanceCyclesUnitLabelLower()}`));
            return;
          }
          this.toastr.success(
            `Se adelantaron ${cycles} ${this.advanceCyclesUnitForCount(cycles)} correctamente`
          );
          this.advanceCyclesConfirmVisible.set(false);
          this.advanceCyclesDialogVisible.set(false);
          this.advancePreview.set(null);
          this.salesLoaded = false;
          this.loadSubscriptionSales();
          this.loadSubscription(id);
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, `No se pudieron adelantar los ${this.advanceCyclesUnitLabelLower()}`));
        }
      });
  }

  advancePreviewSales(): AdvanceBillingCycleSaleSummaryDTO[] {
    return this.advancePreview()?.sales ?? [];
  }

  advancePreviewTotal(): number {
    return this.advancePreviewSales().reduce((sum, s) => sum + (s.total ?? 0), 0);
  }

  /** Unidad de cada ciclo según {@code BillingFrequency} de la suscripción. */
  advanceCyclesUnitLabel(): string {
    const frequency = this.advanceBillingFrequency();
    switch (frequency) {
      case 'DAILY':
        return 'Días';
      case 'WEEKLY':
        return 'Semanas';
      case 'BIWEEKLY':
        return 'Quincenas';
      case 'MONTHLY':
        return 'Meses';
      case 'QUARTERLY':
        return 'Trimestres';
      case 'SEMIANNUALLY':
        return 'Semestres';
      case 'ANNUALLY':
        return 'Años';
      default:
        return 'Ciclos';
    }
  }

  /** Título de acción en plural: Adelantar Meses, Adelantar Días, etc. */
  advanceActionLabel(): string {
    return `Adelantar ${this.advanceCyclesUnitLabel()}`;
  }

  advanceCyclesUnitLabelLower(): string {
    return this.advanceCyclesUnitLabel().toLowerCase();
  }

  advanceCyclesUnitForCount(count: number): string {
    const unit =
      count === 1 ? this.advanceCyclesUnitSingularLabel() : this.advanceCyclesUnitLabel();
    return unit.toLowerCase();
  }

  /** Unidad singular para filas de simulación (p. ej. Mes 1). */
  advanceCyclesUnitSingularLabel(): string {
    const frequency = this.advanceBillingFrequency();
    switch (frequency) {
      case 'DAILY':
        return 'Día';
      case 'WEEKLY':
        return 'Semana';
      case 'BIWEEKLY':
        return 'Quincena';
      case 'MONTHLY':
        return 'Mes';
      case 'QUARTERLY':
        return 'Trimestre';
      case 'SEMIANNUALLY':
        return 'Semestre';
      case 'ANNUALLY':
        return 'Año';
      default:
        return 'Ciclo';
    }
  }

  private advanceBillingFrequency(): string | undefined {
    return (
      this.currentSubscription()?.frequency ??
      (this.paymentTermsForm.getRawValue().frequency as string | undefined)
    );
  }

  getSaleStatusLabel(status: InvoiceStatusEnumValue | string | undefined): string {
    if (!status) return '—';
    return this.saleStatusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getSaleStatusSeverity(
    status: InvoiceStatusEnumValue | string | undefined
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'ISSUED':
      case 'PARTIALLY_PAID':
        return 'warn';
      case 'VOID':
      case 'CANCELLED':
      case 'UNCOLLECTIBLE':
        return 'danger';
      case 'DRAFT':
        return 'info';
      default:
        return 'secondary';
    }
  }

  getSaleStatusIcon(status: InvoiceStatusEnumValue | string | undefined): string {
    switch (status) {
      case 'DRAFT':
        return 'pi pi-file-edit';
      case 'ISSUED':
        return 'pi pi-send';
      case 'PARTIALLY_PAID':
        return 'pi pi-wallet';
      case 'PAID':
        return 'pi pi-check-circle';
      case 'VOID':
        return 'pi pi-ban';
      case 'UNCOLLECTIBLE':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-receipt';
    }
  }

  getSaleStatusIconClasses(status: InvoiceStatusEnumValue | string | undefined): string {
    const base = 'shrink-0 h-10 w-10 rounded-full flex items-center justify-center';
    switch (status) {
      case 'DRAFT':
        return `${base} bg-sky-100 text-sky-600`;
      case 'ISSUED':
        return `${base} bg-amber-100 text-amber-600`;
      case 'PARTIALLY_PAID':
        return `${base} bg-violet-100 text-violet-600`;
      case 'PAID':
        return `${base} bg-green-100 text-green-600`;
      case 'VOID':
        return `${base} bg-slate-100 text-slate-500`;
      case 'UNCOLLECTIBLE':
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getSalePipelineStatusLabel(status: SalePipelineStatusEnumValue | string | undefined): string {
    if (!status) return '—';
    return this.salePipelineStatusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getSaleDelinquencyLabel(d: DelinquencyStatusEnumValue | string | undefined): string {
    if (!d || d === 'CURRENT') return '';
    if (d === 'OVERDUE') return 'Vencida';
    return String(d);
  }

  /** Abre el formulario de factura vinculada si el API expone `invoiceId` o viene en cargos. */
  openLinkedInvoice(): void {
    const id = this.linkedInvoiceId();
    if (id == null) {
      this.toastr.info(
        'Aún no hay factura vinculada. Si la venta genera una factura fiscal, aparecerá aquí cuando el sistema la asocie.'
      );
      return;
    }
    void this.router.navigate(['/invoices', id]);
  }

  /** Abre el detalle de la suscripción vinculada a esta venta. */
  openLinkedSubscription(): void {
    const id = this.linkedSubscriptionId();
    if (id == null) {
      this.toastr.info('Esta venta no tiene una suscripción vinculada.');
      return;
    }
    void this.router.navigate(['/subscriptions', id]);
  }

  /** Copia el enlace público de pago (hash sin vigencia) de la suscripción de cargo recurrente. */
  copySubscriptionPublicLink(): void {
    const id = this.editingSubscriptionId() ?? this.currentSubscription()?.id ?? null;
    if (id == null) return;
    this.subscriptionService.createSubscriptionPublicHash(id).subscribe({
      next: (r) => {
        if (!r.ok || !r.body) {
          this.toastr.error(r.message ?? 'No se pudo generar el enlace de pago');
          return;
        }
        const url = `${window.location.origin}/public/subscriptions/${id}?hash=${encodeURIComponent(r.body)}`;
        void navigator.clipboard.writeText(url).then(
          () => this.toastr.success('Enlace público copiado al portapapeles'),
          () => this.toastr.error('No se pudo copiar el enlace')
        );
      },
      error: (err) => this.toastr.error(err.error?.message ?? 'Error al generar el enlace de pago')
    });
  }

  /** Abre el enlace público de pago (hash sin vigencia) en una pestaña nueva. */
  openSubscriptionPublicLink(): void {
    const id = this.editingSubscriptionId() ?? this.currentSubscription()?.id ?? null;
    if (id == null) return;
    this.subscriptionService.createSubscriptionPublicHash(id).subscribe({
      next: (r) => {
        if (!r.ok || !r.body) {
          this.toastr.error(r.message ?? 'No se pudo generar el enlace de pago');
          return;
        }
        const url = `${window.location.origin}/public/subscriptions/${id}?hash=${encodeURIComponent(r.body)}`;
        window.open(url, '_blank');
      },
      error: (err) => this.toastr.error(err.error?.message ?? 'Error al generar el enlace de pago')
    });
  }

  linkedSubscriptionStatusLabel(): string {
    const status = this.linkedSubscriptionStatus();
    switch (status) {
      case 'PENDING_PAYMENT':
        return 'Pendiente de pago';
      case 'ACTIVE':
        return 'Activa';
      case 'PAUSED':
        return 'Pausada';
      case 'CANCELLED':
        return 'Cancelada';
      case 'EXPIRED':
        return 'Expirada';
      default:
        return status ?? '—';
    }
  }

  /**
   * Intenta abrir el recibo público del primer cargo con link; si no hay datos, recarga cargos y reintenta.
   */
  openFirstReceiptShortcut(): void {
    const tryOpen = (): boolean => {
      const ch = this.firstChargeWithPaymentLink();
      if (ch) {
        this.openChargeReceipt(ch);
        return true;
      }
      return false;
    };
    if (tryOpen()) return;
    const saleId = this.editingSaleId();
    if (saleId == null) return;
    this.chargesLoading.set(true);
    this.saleService
      .getSaleCharges(saleId)
      .pipe(finalize(() => this.chargesLoading.set(false)))
      .subscribe({
        next: (r) => {
          this.charges.set(r.ok && r.body ? r.body : []);
          this.cdr.markForCheck();
          if (!tryOpen()) {
            this.toastr.info('No hay enlaces de recibo disponibles.');
          }
        },
        error: () => {
          this.charges.set([]);
          this.toastr.error('No se pudieron cargar los recibos.');
        }
      });
  }

  /** Primer cargo con link de pago e id (para recibo / copiar enlace). */
  private firstChargeWithPaymentLink(): ChargeDTO | undefined {
    return this.charges().find((c) => Boolean(c.paymentLink?.link && c.id));
  }

  /**
   * Copia al portapapeles la URL pública del recibo del primer cargo con link (misma ruta que al abrir en pestaña).
   */
  copyFirstReceiptPublicLink(): void {
    const copyForCharge = (ch: ChargeDTO): void => {
      if (!ch?.id) return;
      this.receiptLinkCopyLoading.set(true);
      this.invoiceService
        .createChargePublicHash(ch.id, 30)
        .pipe(
          finalize(() => {
            this.receiptLinkCopyLoading.set(false);
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (r) => {
            if (r.ok && r.body) {
              const baseUrl = window.location.origin;
              const url = `${baseUrl}/public/charges/${ch.id}/receipt?hash=${encodeURIComponent(r.body)}`;
              void navigator.clipboard.writeText(url).then(
                () => this.toastr.success('Enlace del recibo copiado al portapapeles'),
                () => this.toastr.error('No se pudo copiar el enlace')
              );
            } else {
              this.toastr.error(r.message ?? 'No se pudo generar el enlace del recibo');
            }
          },
          error: (err) => {
            this.toastr.error(err.error?.message ?? 'Error al generar el enlace del recibo');
          }
        });
    };

    const tryCopy = (): boolean => {
      const ch = this.firstChargeWithPaymentLink();
      if (ch) {
        copyForCharge(ch);
        return true;
      }
      return false;
    };

    if (tryCopy()) return;
    const saleId = this.editingSaleId();
    if (saleId == null) {
      this.toastr.info('No hay enlaces de recibo disponibles.');
      return;
    }
    this.chargesLoading.set(true);
    this.saleService
      .getSaleCharges(saleId)
      .pipe(finalize(() => this.chargesLoading.set(false)))
      .subscribe({
        next: (r) => {
          this.charges.set(r.ok && r.body ? r.body : []);
          this.cdr.markForCheck();
          if (!tryCopy()) {
            this.toastr.info('No hay enlaces de recibo disponibles.');
          }
        },
        error: () => {
          this.charges.set([]);
          this.toastr.error('No se pudieron cargar los recibos.');
        }
      });
  }

  openPaymentLink(url: string): void {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Genera un hash público del cargo y abre la página de recibo público.
   */
  openChargeReceipt(charge: ChargeDTO): void {
    if (!charge?.id) return;
    this.loading.set(true);
    this.invoiceService
      .createChargePublicHash(charge.id, 30)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/public/charges/${charge.id}/receipt?hash=${encodeURIComponent(r.body)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            this.toastr.success('Recibo público abierto en nueva pestaña');
          } else {
            this.toastr.error(r.message ?? 'No se pudo generar el hash del recibo');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al generar el recibo');
        }
      });
  }

  /** URL usable en ventana o iframe a partir de `ReadFileResponse.content` (URL, data: o base64). */
  private normalizeStampPdfSrc(content: string): string {
    const t = content.trim();
    if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('blob:') || t.startsWith('data:')) {
      return t;
    }
    return `data:application/pdf;base64,${t}`;
  }

  private stampPdfContentToSafeUrl(content: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.normalizeStampPdfSrc(content));
  }

  /** Abre el diálogo con visor embebido del PDF del CFDI. */
  openCfdiPdfViewer(): void {
    const id = this.linkedInvoiceId();
    if (id == null || this.cfdiPdfViewerOpening()) return;
    this.cfdiPdfViewerOpening.set(true);
    this.invoiceService
      .getStampPDF(id)
      .pipe(finalize(() => this.cfdiPdfViewerOpening.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body?.content) {
            this.cfdiPdfViewerSafeUrl.set(this.stampPdfContentToSafeUrl(r.body.content));
            this.cfdiPdfViewerVisible.set(true);
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo obtener el PDF del CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al obtener el PDF'));
        }
      });
  }

  onCfdiPdfViewerVisibleChange(visible: boolean): void {
    this.cfdiPdfViewerVisible.set(visible);
    if (!visible) {
      this.cfdiPdfViewerSafeUrl.set(null);
    }
  }

  private sanitizeDownloadFilename(name: string, defaultExt: string): string {
    let s = name.replace(/[/\\?%*:|"<>]/g, '_').trim() || `CFDI${defaultExt}`;
    if (!/\.\w{2,8}$/i.test(s)) {
      s += defaultExt.startsWith('.') ? defaultExt : `.${defaultExt}`;
    }
    return s;
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  downloadStampPDF(): void {
    const id = this.linkedInvoiceId();
    if (id == null) return;
    this.downloadPdfLoading.set(true);
    this.invoiceService
      .downloadStampPdfFile(id)
      .pipe(
        finalize(() => {
          this.downloadPdfLoading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ blob, filename }) => {
          const name = this.sanitizeDownloadFilename(filename, '.pdf');
          this.triggerBrowserDownload(blob, name);
          this.toastr.success('Descarga del PDF iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el PDF'));
        }
      });
  }

  downloadStampXML(): void {
    const id = this.linkedInvoiceId();
    if (id == null) return;
    this.downloadXmlLoading.set(true);
    this.invoiceService
      .downloadStampXmlFile(id)
      .pipe(
        finalize(() => {
          this.downloadXmlLoading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ blob, filename }) => {
          const name = this.sanitizeDownloadFilename(filename, '.xml');
          this.triggerBrowserDownload(blob, name);
          this.toastr.success('Descarga del XML iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el XML'));
        }
      });
  }

  /** Abre el diálogo de confirmación antes de timbrar (Facturar). */
  confirmStampSale(): void {
    if (this.loading() || this.stampSaleLoading() || !this.canStampSaleCfdi() || this.editingSaleId() == null) {
      return;
    }
    this.stampSaleConfirmVisible.set(true);
  }

  cancelStampSaleConfirm(): void {
    this.stampSaleConfirmVisible.set(false);
  }

  /** POST /sales/{id}/stamp y recarga la venta (actualiza `isStamped`). Cierra el diálogo solo si todo OK. */
  executeStampSale(): void {
    const id = this.editingSaleId();
    if (id == null || !this.canStampSaleCfdi() || this.stampSaleLoading()) return;
    this.stampSaleLoading.set(true);
    this.saleService
      .stampSale(id)
      .pipe(
        switchMap((r) => {
          if (!r.ok) {
            return throwError(() => new Error(apiFailureMessage(r, 'No se pudo timbrar el CFDI')));
          }
          return this.saleService.getSaleById(id);
        }),
        finalize(() => this.stampSaleLoading.set(false))
      )
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.stampSaleConfirmVisible.set(false);
            this.currentSale.set(r.body);
            this.patchSale(r.body);
            const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
            this.lines.set(lineVms);
            this.syncInvoicingControlsEnabledState();
            this.toastr.success('CFDI de la venta timbrado correctamente');
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'Error al recargar la venta'));
          }
        },
        error: (err: unknown) => {
          if (err instanceof Error && err.message.trim()) {
            this.toastr.error(err.message);
            return;
          }
          this.toastr.error(httpErrorApiMessage(err, 'Error al timbrar el CFDI'));
        }
      });
  }

  copyPaymentLink(url: string): void {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => this.toastr.success('Link de pago copiado al portapapeles'),
      () => this.toastr.error('No se pudo copiar el link')
    );
  }

  openLineModal(mode: 'create' | 'edit', index?: number): void {
    if (this.isCommercialFieldsReadonly()) return;
    this.loadSatObjetoImps();
    this.lineModalMode.set(mode);
    this.editingLineIndex.set(index ?? null);
    this.lineFiscalDetailsExpanded.set(false);
    if (mode === 'create') {
      this.lineProductSource.set('CATALOG');
      this.lineForm.reset({
        productSelection: null,
        productId: null,
        description: '',
        quantity: 1,
        unitPrice: 0,
        measureUnitSelection: null,
        measureUnitId: null,
        taxProductCodeSelection: null,
        taxProductCodeId: null,
        satObjetoImpSelection: null,
        satObjetoImpId: null,
        taxExempt: false,
        taxInclusive: false,
        measureUnitSearch: '',
        taxProductCodeSearch: ''
      });
    } else if (index !== undefined) {
      const line = this.lines()[index];
      if (line) {
        this.lineProductSource.set(line.productId != null || line.product != null ? 'CATALOG' : 'OPEN');
        const satObjetoImp = line.satObjetoImp ?? (line.satObjetoImpId ? this.satObjetoImpOptions().find((s) => s.id === line.satObjetoImpId) ?? { id: line.satObjetoImpId, name: '' } : null);
        this.lineForm.patchValue({
          productSelection: line.product,
          productId: line.productId,
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          measureUnitSelection: line.measureUnit,
          measureUnitId: line.measureUnitId,
          taxProductCodeSelection: line.taxProductCode,
          taxProductCodeId: line.taxProductCodeId,
          satObjetoImpSelection: satObjetoImp,
          satObjetoImpId: line.satObjetoImpId ?? null,
          taxExempt: line.taxExempt ?? false,
          taxInclusive: line.taxInclusive ?? false
        });
        this.lineFormTaxes.set(line.taxes ?? []);
      }
    }
    this.syncLineProductSelectionDisabled();
    this.lineModalOpen.set(true);
    setTimeout(() => this.lineModalShown.set(true), 10);
    this.cdr.detectChanges();
    setTimeout(() => this.focusLineEntryFirstField(), 100);
  }

  private focusLineEntryFirstField(retry = true): void {
    if (this.lineProductSource() === 'OPEN') {
      this.focusLineDescriptionField(retry);
      return;
    }
    this.focusProductField(retry);
  }

  private focusLineDescriptionField(retry = true): void {
    this.cdr.detectChanges();
    const el = document.getElementById('lineModalDescription');
    if (el && 'focus' in el) {
      (el as HTMLInputElement).focus({ preventScroll: false });
    } else if (retry) {
      setTimeout(() => this.focusLineDescriptionField(false), 250);
    }
  }

  insertLineDescriptionVariable(varKey: string): void {
    const ctrl = this.lineForm.get('description');
    if (!ctrl) return;
    const current = String(ctrl.value ?? '');
    const token = `@${varKey}`;
    const input = document.getElementById('lineModalDescription') as HTMLInputElement | null;
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart ?? current.length;
      const end = input.selectionEnd ?? start;
      ctrl.setValue(`${current.slice(0, start)}${token}${current.slice(end)}`);
      ctrl.markAsDirty();
      const cursor = start + token.length;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(cursor, cursor);
      }, 0);
      return;
    }
    const separator = current.length > 0 && !current.endsWith(' ') ? ' ' : '';
    ctrl.setValue(`${current}${separator}${token}`);
    ctrl.markAsDirty();
  }

  openLineDescriptionVarMenu(event: Event): void {
    this.lineDescriptionVarMenu?.toggle(event);
  }

  private focusProductField(retry = true): void {
    this.cdr.detectChanges();
    const el = this.productLineRef?.nativeElement ?? document.getElementById('productLine');
    const input = el?.querySelector?.('input') as HTMLInputElement | null;
    if (input?.focus) {
      input.focus({ preventScroll: false });
    } else if (retry) {
      setTimeout(() => this.focusProductField(false), 250);
    }
  }

  confirmRemoveLine(index: number): void {
    if (this.isCommercialFieldsReadonly()) return;
    this.confirmationService.confirm({
      message: '¿Eliminar esta línea de la venta?',
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        setTimeout(() => {
          this.removeLine(index);
        }, 0);
      }
    });
  }

  /** Abre el diálogo de confirmación antes de cerrar la venta (ISSUED). */
  confirmCloseSale(): void {
    if (this.loading() || this.isReadonly() || !this.footerDraftSubmitEnabled()) {
      return;
    }
    this.closeSaleConfirmVisible.set(true);
  }

  cancelCloseSaleConfirm(): void {
    this.closeSaleConfirmVisible.set(false);
  }

  confirmCloseSaleSubmit(): void {
    this.closeSaleConfirmVisible.set(false);
    setTimeout(() => this.onSubmitWithStatus('ISSUED'), 0);
  }

  confirmCancelSubscription(): void {
    if (!this.canCancelSubscription() || this.loading() || this.cancelSubscriptionLoading()) {
      return;
    }
    this.cancelSubscriptionConfirmVisible.set(true);
  }

  cancelCancelSubscriptionConfirm(): void {
    if (this.cancelSubscriptionLoading()) return;
    this.cancelSubscriptionConfirmVisible.set(false);
  }

  executeCancelSubscription(): void {
    const id = this.editingSubscriptionId() ?? this.currentSubscription()?.id ?? null;
    if (id == null || this.cancelSubscriptionLoading()) return;

    this.cancelSubscriptionLoading.set(true);
    this.subscriptionService
      .cancelSubscription(id)
      .pipe(finalize(() => this.cancelSubscriptionLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.cancelSubscriptionConfirmVisible.set(false);
            this.toastr.success('Suscripción cancelada');
            this.loadSubscription(id);
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo cancelar la suscripción'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al cancelar la suscripción'));
        }
      });
  }

  closeLineModal(): void {
    this.lineModalShown.set(false);
    setTimeout(() => {
      this.lineModalOpen.set(false);
      this.lineTaxModalOpen.set(false);
    }, 200);
  }

  saveLine(): void {
    if (this.lineForm.invalid) {
      this.lineForm.markAllAsTouched();
      return;
    }

    const v = this.lineForm.getRawValue();
    const qty = Number(v.quantity) ?? 1;
    const unitPrice = Number(v.unitPrice) ?? 0;
    const subtotalInput = qty * unitPrice;
    const taxesInput = this.lineFormTaxes();

    // Si la factura existe, guardar directamente en backend
    const invoiceId = this.editingSaleId();
    if (invoiceId != null) {
      const taxesRequest: CreateSaleLineTaxRequest[] = taxesInput.map((t) => ({
        taxCode: t.taxCode,
        taxRate: t.taxRate,
        taxAmount: t.taxAmount,
        taxBase: t.taxBase,
        applicationType: t.applicationType,
        collectionType: t.collectionType
      }));

      const taxAmount = taxesRequest.reduce((acc, t) => acc + (Number(t.taxAmount) || 0), 0);
      const totalAmount = subtotalInput + taxAmount;

      const request: CreateSaleLineRequest = {
        productId: v.productId,
        description: v.description,
        measureUnitId: v.measureUnitId,
        taxProductCodeId: v.taxProductCodeId,
        satObjetoImpId: v.satObjetoImpId,
        quantity: qty,
        unitPrice: unitPrice,
        subtotal: subtotalInput,
        taxAmount: Math.max(0, taxAmount),
        totalAmount: Math.max(0, totalAmount),
        taxExempt: v.taxExempt ?? false,
        taxInclusive: v.taxInclusive ?? false,
        taxes: taxesRequest,
        metadata: undefined
      };

      this.lineSaving.set(true);
      let obs$;
      const idx = this.editingLineIndex();
      const lineId = idx !== null ? this.lines()[idx]?.id : null;

      if (this.lineModalMode() === 'edit' && lineId != null) {
        obs$ = this.saleService.updateSaleLine(invoiceId, lineId, request);
      } else {
        obs$ = this.saleService.addSaleLine(invoiceId, request);
      }

      obs$.pipe(finalize(() => this.lineSaving.set(false))).subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            this.toastr.success('Línea guardada correctamente');
            this.currentSale.set(res.body);
            this.patchSale(res.body);
            const lineVms = (res.body.lines ?? []).map((l) => this.lineToVm(l));
            this.lines.set(lineVms);
            
            // Actualizar vista y botón
            this.draftButtonRefresh.update((v) => v + 1);
            this.closeLineModal();
            this.cdr.detectChanges();
          } else {
            this.setError(res.message || 'Error al guardar la línea.');
          }
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al guardar la línea.');
        }
      });
      return;
    }

    const previewRequest: PreviewSaleLineRequest = {
      subtotal: subtotalInput,
      taxInclusive: v.taxInclusive ?? false,
      taxes: (taxesInput ?? []).map((t) => ({
        taxCode: t.taxCode,
        taxRate: t.taxRate,
        taxAmount: t.taxAmount,
        taxBase: t.taxBase,
        applicationType: t.applicationType,
        collectionType: t.collectionType
      }))
    };

    this.lineSaving.set(true);
    this.saleService
      .previewSaleLine(previewRequest)
      .pipe(finalize(() => this.lineSaving.set(false)))
      .subscribe({
        next: (res) => {
          if (!res.ok || !res.body) {
            this.setError(res.message || 'No se pudo calcular el preview de la línea.');
            return;
          }
          const lineDto = res.body;
          const taxesFromPreview: CreateSaleLineTaxRequest[] = (lineDto.taxes ?? []).map((t, i) => {
            const inputTax = taxesInput[i];
            return {
              taxCode: t.tax?.taxKey,
              taxRate: t.taxRate,
              taxAmount: t.taxAmount,
              taxBase: t.taxBase,
              applicationType: toLineTaxApplicationTypeForCreate(t.applicationType as TaxApplicationType),
              collectionType: (inputTax?.collectionType ?? t.collectionType ?? 'SELLER_COLLECTS') as 'SELLER_COLLECTS' | 'SELLER_WITHHOLDS'
            };
          });

          const satObjetoImp = (v.satObjetoImpSelection as SATObjetoImpDTO) ?? (v.satObjetoImpId ? this.satObjetoImpOptions().find((s) => s.id === v.satObjetoImpId) ?? { id: v.satObjetoImpId, name: '' } : undefined);
          const vm: SaleLineVm = {
            tempId: `line-${Date.now()}`,
            productId: v.productId,
            product: v.productSelection,
            description: v.description,
            quantity: qty,
            unitPrice,
            subtotal: Number(lineDto.subtotal ?? subtotalInput),
            taxAmount: Number(lineDto.taxAmount ?? 0),
            totalAmount: Number(lineDto.totalAmount ?? subtotalInput),
            measureUnitId: v.measureUnitId,
            measureUnit: v.measureUnitSelection,
            taxProductCodeId: v.taxProductCodeId,
            taxProductCode: v.taxProductCodeSelection,
            satObjetoImpId: v.satObjetoImpId ?? undefined,
            satObjetoImp: satObjetoImp,
            taxExempt: v.taxExempt,
            taxInclusive: v.taxInclusive,
            taxes: taxesFromPreview
          };

          const idx = this.editingLineIndex();
          if (this.lineModalMode() === 'edit' && idx !== null) {
            vm.id = this.lines()[idx].id;
            vm.tempId = this.lines()[idx].tempId;
            this.lines.update((arr) => {
              const next = [...arr];
              next[idx] = vm;
              return next;
            });
          } else {
            this.lines.update((arr) => [...arr, vm]);
          }
          this.markSubscriptionBillingDirty();

          // Patch totales de inmediato para que el botón Guardar borrador no quede deshabilitado por desfase del effect
          const t = this.totals();
          this.generalForm.patchValue(
            { subtotal: t.subtotal, tax: t.tax, traslados: t.traslados, retenciones: t.retenciones, discount: 0, total: t.total },
            { emitEvent: false }
          );
          this.generalForm.updateValueAndValidity({ emitEvent: false });
          this.draftButtonRefresh.update((v) => v + 1);
          this.closeLineModal();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al calcular impuestos de la línea.');
        }
      });
  }

  removeLine(index: number): void {
    const invoiceId = this.editingSaleId();
    const line = this.lines()[index];
    const lineId = line?.id;

    // Si la factura existe en backend y la línea tiene id, llamar al API
    if (invoiceId != null && typeof lineId === 'number') {
      this.lineRemoving.set(true);
      this.saleService
        .removeSaleLine(invoiceId, lineId)
        .pipe(finalize(() => this.lineRemoving.set(false)))
        .subscribe({
          next: (r) => {
            if (r.ok && r.body) {
              this.currentSale.set(r.body);
              this.patchSale(r.body);
              const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
              this.lines.set(lineVms);
              this.expandedLineIndices.set(new Set());
              if (this.lineModalOpen()) this.closeLineModal();
              this.toastr.success('Línea eliminada correctamente.');
            } else {
              this.setError(r.message || 'Error al eliminar la línea.');
            }
          },
          error: (err) => {
            this.setError(err.error?.message || 'Error al eliminar la línea.');
          }
        });
      return;
    }

    // Línea local (sin id): eliminar solo en memoria
    this.lines.update((arr) => arr.filter((_, i) => i !== index));
    this.markSubscriptionBillingDirty();
    this.expandedLineIndices.update((set) => {
      return new Set(
        [...set].filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
      );
    });
    this.draftButtonRefresh.update((v) => v + 1);
    this.cdr.detectChanges();
    if (this.lineModalOpen()) this.closeLineModal();
  }

  toggleLineExpanded(index: number): void {
    this.expandedLineIndices.update((set) => {
      const next = new Set(set);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  isLineExpanded(index: number): boolean {
    return this.expandedLineIndices().has(index);
  }

  editLineTaxInModal(taxIndex: number | null): void {
    this.editingLineTaxIndex.set(taxIndex);
    const taxes = this.lineFormTaxes();
    if (taxIndex !== null && taxes[taxIndex]) {
      const t = taxes[taxIndex];
      const taxOpt = this.taxOptions().find((x) => x.taxKey === t.taxCode) ?? null;
      this.lineTaxForm.patchValue({
        taxSelection: taxOpt,
        taxCode: t.taxCode,
        taxRate: t.taxRate ?? null,
        taxAmount: t.taxAmount ?? null,
        taxBase: t.taxBase ?? '',
        applicationType: t.applicationType ?? 'TAXABLE',
        collectionType: t.collectionType ?? 'SELLER_COLLECTS'
      });
    } else {
      this.lineTaxForm.reset({
        taxSelection: null,
        taxCode: '',
        taxRate: null,
        taxAmount: null,
        taxBase: '',
        applicationType: 'TAXABLE',
        collectionType: 'SELLER_COLLECTS'
      });
    }
    this.lineTaxModalOpen.set(true);
    setTimeout(() => this.lineTaxModalShown.set(true), 10);
  }

  closeLineTaxModal(): void {
    this.lineTaxModalShown.set(false);
    setTimeout(() => {
      this.lineTaxModalOpen.set(false);
      this.editingLineTaxIndex.set(null);
    }, 200);
  }

  private setupLineTaxFormExclusiveFields(): void {
    const taxRateCtrl = this.lineTaxForm.get('taxRate');
    const taxAmountCtrl = this.lineTaxForm.get('taxAmount');
    if (!taxRateCtrl || !taxAmountCtrl) return;

    taxRateCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.hasLineTaxValue(value)) {
          taxAmountCtrl.setValue(null, { emitEvent: false });
        }
      });

    taxAmountCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.hasLineTaxValue(value)) {
          taxRateCtrl.setValue(null, { emitEvent: false });
        }
      });
  }

  private hasLineTaxValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  saveLineTaxInModal(): void {
    if (this.lineTaxForm.invalid) return;
    const v = this.lineTaxForm.getRawValue();
    const tax = v.taxSelection as TaxDTO | null;
    if (!tax) return;

    const req: CreateSaleLineTaxRequest = {
      taxCode: tax.taxKey,
      taxRate: v.taxRate != null && v.taxRate !== '' ? Number(v.taxRate) : undefined,
      taxAmount: v.taxAmount != null && v.taxAmount !== '' ? Number(v.taxAmount) : undefined,
      taxBase: v.taxBase ? Number(v.taxBase) : undefined,
      applicationType: v.applicationType,
      collectionType: v.collectionType ?? 'SELLER_COLLECTS'
    };

    const ti = this.editingLineTaxIndex();
    this.lineFormTaxes.update((arr) => {
      const next = [...arr];
      if (ti !== null) {
        next[ti] = req;
      } else {
        next.push(req);
      }
      return next;
    });

    this.closeLineTaxModal();
  }

  removeLineTaxInModal(taxIndex: number): void {
    this.lineFormTaxes.update((arr) => arr.filter((_, i) => i !== taxIndex));
  }

  getTaxApplicationLabel(t: TaxApplicationType): string {
    return this.taxApplicationOptions.find((o) => o.value === t)?.label ?? t;
  }

  getTaxCollectionTypeLabel(type: string | undefined): string {
    return this.taxCollectionTypeOptions.find((o) => o.value === type)?.label ?? '-';
  }

  /** Devuelve "Nombre (código)" del impuesto, o solo el código si no se encuentra. */
  getTaxDisplayWithCode(taxCode: string | undefined): string {
    if (!taxCode) return '-';
    const tax = this.taxOptions().find((x) => x.taxKey === taxCode);
    if (tax?.displayName) return `${taxCode} - ${tax.displayName}`;
    return taxCode;
  }

  /** Formato de tasa para vista: si viene como decimal (0.16) muestra 16%, si viene como entero (16) muestra 16%. */
  formatTaxRate(rate: number | undefined | null): string {
    if (rate == null) return '-';
    if (rate > 0 && rate <= 1) return `${(rate * 100).toFixed(2)}%`;
    return `${Number(rate).toFixed(2)}%`;
  }

  private setError(msg: string): void {
    this.error.set(msg);
    this.toastr.error(msg);
  }
}
